import { Maximize2, Minimize2, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Button, Card, CardContent, LoadingSpinner, Tabs } from '../components/ui';
import { ApiDocsPanel } from '../components/tools/ApiDocsPanel';
import { EmbedKeyPanel } from '../components/tools/EmbedKeyPanel';
import { useAuth, useOrganization, usePage } from '../contexts';
import { ToolTemplate } from '../types';
import { toolService } from '../services';
import { toolRuntimeService } from '../services/ToolRuntimeService';
import { toolToast } from '../utils/toolToast';
import {
  RuntimeActionType,
  resolveRuntimeActionErrorMessage,
  resolveRuntimeActionSuccessMessage,
} from './toolViewerRuntimeAlerts';
import {
  normalizeRuntimeActionRequest,
  resolveRuntimeActionData,
} from './toolViewerRuntimeActionNormalizer';
import { coerceRecordPayload } from './toolViewerRuntimePayloadCoercer';
import {
  mergeTemplateStateWithRuntimeEntities,
  normalizeRuntimeRecord,
} from './toolViewerRuntimeBridge';

const RUNTIME_ACTIONS_THAT_PERSIST_SCREEN_STATE: RuntimeActionType[] = [
  'record.create',
  'record.update',
  'record.delete',
];
const RUNTIME_BRIDGE_AUTO_IDEMPOTENCY_ENABLED = true;

const wait = (ms: number) =>
  new Promise<void>(resolve => {
    window.setTimeout(resolve, ms);
  });

const toNonEmptyString = (value: unknown): string | undefined => {
  const normalized = String(value || '').trim();
  return normalized ? normalized : undefined;
};

const toLegacyRuntimeRecord = (row: Record<string, unknown>): Record<string, unknown> => {
  const nestedData =
    row && typeof row.data === 'object' && row.data
      ? (row.data as Record<string, unknown>)
      : {};
  const recordId = String(row.id || row.record_id || '').trim();
  const externalId = String(row.external_id || nestedData.external_id || '').trim();
  const fallbackId = String(nestedData.id || '').trim();
  return {
    ...row,
    ...nestedData,
    id: recordId || fallbackId || externalId || null,
    record_id: recordId || null,
    external_id: externalId || null,
    data: nestedData,
  };
};

const normalizeRuntimeResultForIframe = (
  action: RuntimeActionType,
  rawResult: unknown
): Record<string, unknown> => {
  const result =
    rawResult && typeof rawResult === 'object' && !Array.isArray(rawResult)
      ? ({ ...(rawResult as Record<string, unknown>) } as Record<string, unknown>)
      : {};

  if (action === 'record.list') {
    const sourceItems = Array.isArray(result.items)
      ? result.items
      : Array.isArray(rawResult)
        ? rawResult
        : [];
    const items = sourceItems
      .map(item =>
        item && typeof item === 'object' && !Array.isArray(item)
          ? toLegacyRuntimeRecord(item as Record<string, unknown>)
          : null
      )
      .filter((item): item is Record<string, unknown> => Boolean(item));
    return {
      ...result,
      items,
    };
  }

  if (action === 'record.get' || action === 'record.create' || action === 'record.update') {
    const rawRecord =
      result.record && typeof result.record === 'object' && !Array.isArray(result.record)
        ? (result.record as Record<string, unknown>)
        : {};
    const normalizedRecord = toLegacyRuntimeRecord(rawRecord);
    return {
      ...result,
      ...normalizedRecord,
      record: normalizedRecord,
      id: normalizedRecord.id,
    };
  }

  return result;
};

const RUNTIME_DOMAIN_REGEX =
  /<script[^>]*id=["']devspott-runtime-domain["'][^>]*>([\s\S]*?)<\/script>/i;
const SCREEN_STATE_ENTITY_TYPE = 'screen_state';

interface TemplateStateResult {
  hasContract: boolean;
  responded: boolean;
  state: Record<string, unknown> | null;
}

interface TemplateStateApplyResult {
  applied: boolean;
  hasContract: boolean;
  responded: boolean;
}

const extractRuntimeEntityNamesFromTool = (currentTool: ToolTemplate | null): string[] => {
  if (!currentTool) {
    return [];
  }

  const seen = new Set<string>();
  const names: string[] = [];
  const addName = (value: unknown) => {
    const normalized = String(value || '').trim();
    if (!normalized || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    names.push(normalized);
  };

  const html = String(currentTool.html_content || '');
  const domainMatch = html.match(RUNTIME_DOMAIN_REGEX);
  if (domainMatch?.[1]) {
    try {
      const parsed = JSON.parse(domainMatch[1]);
      const entities = Array.isArray(parsed?.entities) ? parsed.entities : [];
      entities.forEach((entity: unknown) => {
        if (entity && typeof entity === 'object') {
          addName((entity as { name?: unknown }).name);
        }
      });
    } catch {
      // ignore malformed runtime domain block
    }
  }

  const schema = currentTool.schema;
  if (schema && typeof schema === 'object') {
    const schemaEntities = (schema as { entities?: unknown }).entities;
    if (Array.isArray(schemaEntities)) {
      schemaEntities.forEach((entity: unknown) => {
        if (entity && typeof entity === 'object') {
          addName((entity as { name?: unknown }).name);
        }
      });
    }
  }

  return names;
};

type RuntimeActionMessagePayload = {
  source?: string;
  type?: string;
  messageId?: string;
  runtime_action?: RuntimeActionType;
  request_id?: string;
  entity_type?: string;
  record_id?: string;
  external_id?: string;
  data?: Record<string, unknown>;
  payload?: Record<string, unknown>;
};

const printHtmlWithoutPopup = (html: string, onError: () => void): void => {
  if (!html || !html.trim()) {
    onError();
    return;
  }

  const printFrame = document.createElement('iframe');
  printFrame.style.position = 'fixed';
  printFrame.style.right = '0';
  printFrame.style.bottom = '0';
  printFrame.style.width = '0';
  printFrame.style.height = '0';
  printFrame.style.border = '0';
  printFrame.style.opacity = '0';
  printFrame.setAttribute('aria-hidden', 'true');

  const cleanup = () => {
    if (printFrame.parentNode) {
      printFrame.parentNode.removeChild(printFrame);
    }
  };

  document.body.appendChild(printFrame);

  const doc = printFrame.contentDocument;
  if (!doc) {
    cleanup();
    onError();
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  printFrame.onload = () => {
    const frameWindow = printFrame.contentWindow;
    if (!frameWindow) {
      cleanup();
      onError();
      return;
    }

    const fallbackCleanupTimeout = window.setTimeout(cleanup, 2000);
    frameWindow.addEventListener(
      'afterprint',
      () => {
        window.clearTimeout(fallbackCleanupTimeout);
        cleanup();
      },
      { once: true }
    );

    frameWindow.focus();
    window.setTimeout(() => {
      try {
        frameWindow.print();
      } catch {
        window.clearTimeout(fallbackCleanupTimeout);
        cleanup();
        onError();
      }
    }, 80);
  };
};

const buildCanvasDocument = (html: string, messageId: string): string => {
  const bridgeScript = `
    <script data-devspott-canvas-bridge="1">
      (function () {
        var CHANNEL_SOURCE = 'devspott-canvas';
        var CHANNEL_PARENT_SOURCE = 'devspott-canvas-parent';
        var MESSAGE_ID = ${JSON.stringify(messageId)};
        var INPUT_SELECTOR = '[data-field]';

        function readSingleEntityTypeFromDomainScript() {
          try {
            var el = document.getElementById('devspott-runtime-domain');
            if (!el) {
              return '';
            }
            var raw = (el.textContent || '').trim();
            if (!raw) {
              return '';
            }
            var parsed = JSON.parse(raw);
            var entities = parsed && parsed.entities;
            if (!Array.isArray(entities) || entities.length !== 1) {
              return '';
            }
            var n = entities[0] && entities[0].name;
            return typeof n === 'string' && n.trim() ? n.trim() : '';
          } catch (e) {
            return '';
          }
        }
        var DOMAIN_SINGLE_ENTITY_TYPE = readSingleEntityTypeFromDomainScript();
        var RUNTIME_RECORDS_CACHE = [];
        var RUNTIME_RECORDS_VIEW = {
          entity_filter: '',
          search: '',
          status_filter: '',
          category_filter: '',
          date_from: '',
          date_to: '',
          page: 1,
          page_size: 8,
          sort_by: 'entity_type',
          sort_dir: 'asc',
        };
        var RUNTIME_RECORDS_VIEW_SCOPES = {};
        var RUNTIME_RECORDS_VIEW_JUST_RESTORED = false;
        var RUNTIME_CREATE_IDEMPOTENCY_CACHE = new WeakMap();
        var RUNTIME_CREATE_REQUEST_ID_TO_KEY = {};

        function normalizeValue(el) {
          var tag = (el.tagName || '').toLowerCase();
          var type = (el.getAttribute('type') || '').toLowerCase();

          if (tag === 'input' && type === 'checkbox') {
            return !!el.checked;
          }

          if (tag === 'input' && type === 'radio') {
            if (!el.checked) return undefined;
            return el.value;
          }

          if (tag === 'input' || tag === 'textarea' || tag === 'select') {
            return el.value;
          }

          if (el.hasAttribute('data-value')) {
            return el.getAttribute('data-value');
          }

          return (el.textContent || '').trim();
        }

        function collectValues() {
          var values = {};
          var nodes = document.querySelectorAll(INPUT_SELECTOR);

          nodes.forEach(function (el) {
            var fieldName = el.getAttribute('data-field');

            if (!fieldName) return;

            var value = normalizeValue(el);
            if (typeof value === 'undefined') return;
            values[fieldName] = value;
          });

          return values;
        }

        function buildDomSnapshot() {
          var sourceRoot = document.documentElement;
          if (!sourceRoot) {
            return '';
          }

          var cloneRoot = sourceRoot.cloneNode(true);
          var sourceControls = sourceRoot.querySelectorAll('input, textarea, select');
          var cloneControls = cloneRoot.querySelectorAll('input, textarea, select');

          for (var i = 0; i < sourceControls.length; i += 1) {
            var sourceControl = sourceControls[i];
            var cloneControl = cloneControls[i];
            if (!cloneControl) {
              continue;
            }

            var tag = (sourceControl.tagName || '').toLowerCase();
            var inputType = (sourceControl.getAttribute('type') || '').toLowerCase();

            if (tag === 'input') {
              if (inputType === 'checkbox' || inputType === 'radio') {
                if (sourceControl.checked) {
                  cloneControl.setAttribute('checked', 'checked');
                } else {
                  cloneControl.removeAttribute('checked');
                }
              } else {
                cloneControl.setAttribute('value', sourceControl.value || '');
              }
              continue;
            }

            if (tag === 'textarea') {
              cloneControl.textContent = sourceControl.value || '';
              continue;
            }

            if (tag === 'select') {
              var sourceOptions = sourceControl.options || [];
              var cloneOptions = cloneControl.options || [];
              for (var optionIndex = 0; optionIndex < cloneOptions.length; optionIndex += 1) {
                var sourceOption = sourceOptions[optionIndex];
                var cloneOption = cloneOptions[optionIndex];
                if (!sourceOption || !cloneOption) {
                  continue;
                }
                if (sourceOption.selected) {
                  cloneOption.setAttribute('selected', 'selected');
                } else {
                  cloneOption.removeAttribute('selected');
                }
              }
            }
          }

          var bridgeScripts = cloneRoot.querySelectorAll('script[data-devspott-canvas-bridge]');
          bridgeScripts.forEach(function (node) {
            if (node && node.parentNode) {
              node.parentNode.removeChild(node);
            }
          });

          return '<!DOCTYPE html>\\n' + cloneRoot.outerHTML;
        }

        function getTemplateState() {
          function toSerializable(value) {
            try {
              return JSON.parse(JSON.stringify(value));
            } catch (error) {
              return null;
            }
          }

          function ensureCanvasKey(canvas, index) {
            if (!canvas) return '';
            var existing = canvas.getAttribute('data-devspott-chart-key');
            if (existing) return existing;
            var key = (canvas.id && String(canvas.id).trim()) || ('chart-' + String(index || 0));
            canvas.setAttribute('data-devspott-chart-key', key);
            return key;
          }

          function collectChartJsState() {
            var chartState = [];
            try {
              if (!window.Chart) return chartState;
              var instances = window.Chart.instances || {};
              var list = [];
              if (typeof instances.forEach === 'function') {
                instances.forEach(function (chart) {
                  list.push(chart);
                });
              } else {
                Object.keys(instances).forEach(function (key) {
                  if (instances[key]) list.push(instances[key]);
                });
              }
              list.forEach(function (chart, index) {
                if (!chart || !chart.canvas) return;
                var canvas = chart.canvas;
                var chartKey = ensureCanvasKey(canvas, index);
                var entry = {
                  key: chartKey,
                  canvas_id: canvas.id || null,
                  type: chart.config && chart.config.type ? chart.config.type : chart.type || null,
                  data: toSerializable(chart.data || null),
                  options: toSerializable(chart.options || null),
                };
                if (entry.data) chartState.push(entry);
              });
            } catch (error) {}
            return chartState;
          }

          if (typeof window.__devspottGetState === 'function') {
            try {
              var state = window.__devspottGetState();
              if (state && typeof state === 'object') {
                state.__devspottRuntimeRecordsView = serializeRuntimeRecordsViewState();
                if (!Array.isArray(state.chartjs_state) || state.chartjs_state.length === 0) {
                  state.chartjs_state = collectChartJsState();
                }
                if (!state.__devspottContractVersion) {
                  state.__devspottContractVersion = 2;
                }
                return { has_contract: true, state: state };
              }
              return { has_contract: true, state: null };
            } catch (error) {
              return { has_contract: true, state: null };
            }
          }
          return { has_contract: false, state: null };
        }

        function setTemplateState(state) {
          function toSerializable(value) {
            try {
              return JSON.parse(JSON.stringify(value));
            } catch (error) {
              return null;
            }
          }

          function restoreDomSnapshot(snapshotHtml) {
            if (!snapshotHtml || typeof snapshotHtml !== 'string') {
              return false;
            }
            try {
              var parser = new DOMParser();
              var parsed = parser.parseFromString(snapshotHtml, 'text/html');
              if (!parsed || !parsed.body || !document.body) {
                return false;
              }
              var incomingBody = parsed.body.cloneNode(true);
              if (!incomingBody) {
                return false;
              }

              var bridgeScripts = incomingBody.querySelectorAll('script[data-devspott-canvas-bridge]');
              bridgeScripts.forEach(function (node) {
                if (node && node.parentNode) {
                  node.parentNode.removeChild(node);
                }
              });

              document.body.innerHTML = incomingBody.innerHTML;
              if (parsed.title) {
                document.title = parsed.title;
              }
              return true;
            } catch (error) {
              return false;
            }
          }

          function findChartByCanvas(canvas) {
            if (!canvas || !window.Chart) return null;
            try {
              if (typeof window.Chart.getChart === 'function') {
                return window.Chart.getChart(canvas) || null;
              }
            } catch (error) {}
            try {
              var instances = window.Chart.instances || {};
              var found = null;
              Object.keys(instances).forEach(function (key) {
                var chart = instances[key];
                if (!found && chart && chart.canvas === canvas) found = chart;
              });
              return found;
            } catch (error) {
              return null;
            }
          }

          function applyChartJsState(chartState) {
            if (!Array.isArray(chartState) || !window.Chart) return false;
            var applied = false;
            chartState.forEach(function (entry, index) {
              if (!entry || !entry.data) return;
              var canvas = null;
              if (entry.canvas_id) {
                canvas = document.getElementById(entry.canvas_id);
              }
              if (!canvas && entry.key) {
                canvas = document.querySelector(
                  '[data-devspott-chart-key="' + String(entry.key) + '"]'
                );
              }
              if (!canvas) {
                var byIndex = document.querySelectorAll('canvas');
                canvas = byIndex[index] || null;
              }
              if (!canvas) return;

              var applyData = toSerializable(entry.data);
              var applyOptions = toSerializable(entry.options);
              var existing = findChartByCanvas(canvas);
              if (existing) {
                existing.data = applyData || existing.data;
                if (applyOptions) existing.options = applyOptions;
                existing.update();
                applied = true;
                return;
              }
              try {
                var ctx = canvas.getContext('2d');
                if (!ctx) return;
                var type = entry.type || 'bar';
                new window.Chart(ctx, {
                  type: type,
                  data: applyData || { labels: [], datasets: [] },
                  options: applyOptions || {},
                });
                applied = true;
              } catch (error) {}
            });
            return applied;
          }

          var hasContract = typeof window.__devspottSetState === 'function';
          var appliedByContract = false;
          var isStateObject = state && typeof state === 'object';
          var contractVersion = isStateObject
            ? Number(state.__devspottContractVersion || 0)
            : 0;
          var hasChartJsState =
            isStateObject &&
            Array.isArray(state.chartjs_state) &&
            state.chartjs_state.length > 0;
          var shouldForceSnapshotFallback =
            isStateObject &&
            !!state.dom_snapshot &&
            (contractVersion < 2 || !hasChartJsState);
          if (typeof window.__devspottSetState === 'function') {
            try {
              window.__devspottSetState(state || {});
              appliedByContract = true;
            } catch (error) {
              appliedByContract = false;
            }
          }

          var appliedBySnapshot = false;
          if (
            state &&
            typeof state === 'object' &&
            state.dom_snapshot &&
            (!appliedByContract || shouldForceSnapshotFallback)
          ) {
            appliedBySnapshot = restoreDomSnapshot(state.dom_snapshot);
          }

          if (state && typeof state === 'object' && Array.isArray(state.chartjs_state)) {
            var tries = 0;
            function tryApplyCharts() {
              tries += 1;
              var done = applyChartJsState(state.chartjs_state);
              if (!done && tries < 5) {
                setTimeout(tryApplyCharts, 140 * tries);
              }
            }
            tryApplyCharts();
          }

          if (
            state &&
            typeof state === 'object' &&
            isPlainObject(state.__devspottRuntimeRecordsView)
          ) {
            restoreRuntimeRecordsViewState(state.__devspottRuntimeRecordsView);
          }

          if (state && typeof state === 'object') {
            renderRuntimeRecordsTable(state);
          }

          return {
            applied: !!(appliedByContract || appliedBySnapshot),
            has_contract: hasContract,
          };
        }

        function applyValue(el, value) {
          var tag = (el.tagName || '').toLowerCase();
          var type = (el.getAttribute('type') || '').toLowerCase();

          if (tag === 'input' && type === 'checkbox') {
            el.checked = !!value;
            return;
          }

          if (tag === 'input' && type === 'radio') {
            el.checked = String(value) === String(el.value);
            return;
          }

          if (tag === 'input' || tag === 'textarea' || tag === 'select') {
            el.value = value == null ? '' : String(value);
            return;
          }

          if (el.hasAttribute('data-value')) {
            el.setAttribute('data-value', value == null ? '' : String(value));
          }

          el.textContent = value == null ? '' : String(value);
        }

        function applyValues(values) {
          if (!values || typeof values !== 'object') {
            return;
          }

          var nodes = document.querySelectorAll(INPUT_SELECTOR);
          nodes.forEach(function (el) {
            var fieldName = el.getAttribute('data-field');
            if (!fieldName || !(fieldName in values)) return;
            applyValue(el, values[fieldName]);
          });
        }

        function postValues() {
          var state = readContractState();
          if (state) {
            renderRuntimeRecordsTable(state);
          }
          window.parent.postMessage(
            {
              source: CHANNEL_SOURCE,
              type: 'fields_snapshot',
              messageId: MESSAGE_ID,
              values: collectValues(),
            },
            '*'
          );
        }

        function postDomSnapshot() {
          window.parent.postMessage(
            {
              source: CHANNEL_SOURCE,
              type: 'dom_snapshot',
              messageId: MESSAGE_ID,
              html: buildDomSnapshot(),
            },
            '*'
          );
        }

        function postTemplateState() {
          var result = getTemplateState();
          window.parent.postMessage(
            {
              source: CHANNEL_SOURCE,
              type: 'template_state',
              messageId: MESSAGE_ID,
              has_contract: !!result.has_contract,
              state: result.state,
            },
            '*'
          );
        }

        function postRuntimeAction(runtimeAction, params) {
          var requestId = (window.crypto && crypto.randomUUID)
            ? crypto.randomUUID()
            : ('req_' + Date.now() + '_' + Math.random().toString(16).slice(2));
          var normalizedParams = params && typeof params === 'object' ? params : {};
          window.parent.postMessage(
            {
              source: CHANNEL_SOURCE,
              type: 'runtime_action',
              messageId: MESSAGE_ID,
              runtime_action: runtimeAction,
              request_id: requestId,
              entity_type: normalizedParams.entity_type,
              record_id: normalizedParams.record_id,
              external_id: normalizedParams.external_id,
              data:
                normalizedParams.data && typeof normalizedParams.data === 'object'
                  ? normalizedParams.data
                  : normalizedParams,
            },
            '*'
          );
          return requestId;
        }

        function emitRuntimeTelemetry(eventName, context) {
          var normalizedEventName = String(eventName || '').trim();
          if (!normalizedEventName) {
            return null;
          }
          var normalizedContext = isPlainObject(context) ? context : {};
          var telemetrySource = String(normalizedContext.source || 'tool_viewer.runtime').trim();
          if (normalizedContext.source) {
            delete normalizedContext.source;
          }
          return postRuntimeAction('runtime.telemetry', {
            data: {
              event_name: normalizedEventName,
              contract_version: 1,
              source: telemetrySource,
              context: normalizedContext,
            },
          });
        }

        function toSerializable(value) {
          try {
            return JSON.parse(JSON.stringify(value));
          } catch (error) {
            return null;
          }
        }

        function isPlainObject(value) {
          return !!value && typeof value === 'object' && !Array.isArray(value);
        }

        function readContractState() {
          if (typeof window.__devspottGetState !== 'function') {
            return null;
          }
          try {
            var state = window.__devspottGetState();
            if (!isPlainObject(state)) {
              return null;
            }
            var serializedState = toSerializable(state);
            return isPlainObject(serializedState) ? serializedState : null;
          } catch (error) {
            return null;
          }
        }

        function normalizeIdentity(value) {
          return value == null ? '' : String(value).trim();
        }

        function readRuntimeEntityTypeAttr(node) {
          if (!node || typeof node.getAttribute !== 'function') {
            return '';
          }
          return normalizeIdentity(
            node.getAttribute('data-entity-type') || node.getAttribute('data-entity-name')
          );
        }

        function readRuntimeRecordIdAttr(node) {
          if (!node || typeof node.getAttribute !== 'function') {
            return '';
          }
          return normalizeIdentity(node.getAttribute('data-record-id') || node.getAttribute('data-id'));
        }

        function stableStringify(value) {
          if (!value || typeof value !== 'object') {
            return JSON.stringify(value);
          }
          if (Array.isArray(value)) {
            return (
              '[' +
              value
                .map(function (item) {
                  return stableStringify(item);
                })
                .join(',') +
              ']'
            );
          }
          var keys = Object.keys(value).sort();
          return (
            '{' +
            keys
              .map(function (key) {
                return JSON.stringify(key) + ':' + stableStringify(value[key]);
              })
              .join(',') +
            '}'
          );
        }

        function hashDjb2(text) {
          var hash = 5381;
          var input = String(text || '');
          for (var i = 0; i < input.length; i += 1) {
            hash = (hash * 33) ^ input.charCodeAt(i);
          }
          return (hash >>> 0).toString(16);
        }

        function resolveOrCreateIdempotencyKey(runtimeNode, runtimeData) {
          if (!runtimeNode || !isPlainObject(runtimeData)) {
            return '';
          }
          if (normalizeIdentity(runtimeData.idempotency_key)) {
            return normalizeIdentity(runtimeData.idempotency_key);
          }
          var cacheEntry = RUNTIME_CREATE_IDEMPOTENCY_CACHE.get(runtimeNode);
          var now = Date.now();
          var payloadHash = hashDjb2(stableStringify(runtimeData));
          if (
            cacheEntry &&
            cacheEntry.hash === payloadHash &&
            cacheEntry.expires_at &&
            cacheEntry.expires_at > now &&
            cacheEntry.key
          ) {
            return String(cacheEntry.key);
          }
          var generatedKey = (window.crypto && crypto.randomUUID)
            ? crypto.randomUUID()
            : 'idem_' + now + '_' + Math.random().toString(16).slice(2);
          RUNTIME_CREATE_IDEMPOTENCY_CACHE.set(runtimeNode, {
            hash: payloadHash,
            key: generatedKey,
            expires_at: now + 60000,
          });
          return generatedKey;
        }

        function buildRuntimeViewDefaults() {
          return {
            search: '',
            status_filter: '',
            category_filter: '',
            date_from: '',
            date_to: '',
            page: 1,
            page_size: 8,
            sort_by: 'entity_type',
            sort_dir: 'asc',
          };
        }

        function getRuntimeViewScopeKey(entityFilter) {
          var normalized = normalizeIdentity(entityFilter);
          return normalized ? 'entity:' + normalized : 'entity:__all__';
        }

        function copyRuntimeViewScopeSnapshot() {
          return {
            search: String(RUNTIME_RECORDS_VIEW.search || ''),
            status_filter: String(RUNTIME_RECORDS_VIEW.status_filter || ''),
            category_filter: String(RUNTIME_RECORDS_VIEW.category_filter || ''),
            date_from: String(RUNTIME_RECORDS_VIEW.date_from || ''),
            date_to: String(RUNTIME_RECORDS_VIEW.date_to || ''),
            page: Math.max(1, parseInt(String(RUNTIME_RECORDS_VIEW.page || 1), 10) || 1),
            page_size: Math.max(1, parseInt(String(RUNTIME_RECORDS_VIEW.page_size || 8), 10) || 8),
            sort_by: String(RUNTIME_RECORDS_VIEW.sort_by || 'entity_type'),
            sort_dir: String(RUNTIME_RECORDS_VIEW.sort_dir || 'asc') === 'desc' ? 'desc' : 'asc',
          };
        }

        function saveRuntimeViewScope() {
          var scopeKey = getRuntimeViewScopeKey(RUNTIME_RECORDS_VIEW.entity_filter);
          RUNTIME_RECORDS_VIEW_SCOPES[scopeKey] = copyRuntimeViewScopeSnapshot();
        }

        function applyRuntimeViewScope(entityFilter) {
          var normalizedEntityFilter = normalizeIdentity(entityFilter);
          var defaults = buildRuntimeViewDefaults();
          var scopeKey = getRuntimeViewScopeKey(normalizedEntityFilter);
          var storedScope = isPlainObject(RUNTIME_RECORDS_VIEW_SCOPES[scopeKey])
            ? RUNTIME_RECORDS_VIEW_SCOPES[scopeKey]
            : {};
          RUNTIME_RECORDS_VIEW.entity_filter = normalizedEntityFilter;
          RUNTIME_RECORDS_VIEW.search = String(storedScope.search || defaults.search);
          RUNTIME_RECORDS_VIEW.status_filter = String(
            storedScope.status_filter || defaults.status_filter
          );
          RUNTIME_RECORDS_VIEW.category_filter = String(
            storedScope.category_filter || defaults.category_filter
          );
          RUNTIME_RECORDS_VIEW.date_from = String(storedScope.date_from || defaults.date_from);
          RUNTIME_RECORDS_VIEW.date_to = String(storedScope.date_to || defaults.date_to);
          RUNTIME_RECORDS_VIEW.page = Math.max(
            1,
            parseInt(String(storedScope.page || defaults.page), 10) || defaults.page
          );
          RUNTIME_RECORDS_VIEW.page_size = Math.max(
            1,
            parseInt(String(storedScope.page_size || defaults.page_size), 10) ||
              defaults.page_size
          );
          RUNTIME_RECORDS_VIEW.sort_by = String(storedScope.sort_by || defaults.sort_by);
          RUNTIME_RECORDS_VIEW.sort_dir =
            String(storedScope.sort_dir || defaults.sort_dir) === 'desc' ? 'desc' : 'asc';
        }

        function serializeRuntimeRecordsViewState() {
          saveRuntimeViewScope();
          return {
            active_view: {
              entity_filter: String(RUNTIME_RECORDS_VIEW.entity_filter || ''),
              search: String(RUNTIME_RECORDS_VIEW.search || ''),
              status_filter: String(RUNTIME_RECORDS_VIEW.status_filter || ''),
              category_filter: String(RUNTIME_RECORDS_VIEW.category_filter || ''),
              date_from: String(RUNTIME_RECORDS_VIEW.date_from || ''),
              date_to: String(RUNTIME_RECORDS_VIEW.date_to || ''),
              page: Math.max(1, parseInt(String(RUNTIME_RECORDS_VIEW.page || 1), 10) || 1),
              page_size: Math.max(1, parseInt(String(RUNTIME_RECORDS_VIEW.page_size || 8), 10) || 8),
              sort_by: String(RUNTIME_RECORDS_VIEW.sort_by || 'entity_type'),
              sort_dir: String(RUNTIME_RECORDS_VIEW.sort_dir || 'asc') === 'desc' ? 'desc' : 'asc',
            },
            scopes: toSerializable(RUNTIME_RECORDS_VIEW_SCOPES) || {},
          };
        }

        function restoreRuntimeRecordsViewState(rawState) {
          if (!isPlainObject(rawState)) {
            return;
          }
          var incomingScopes = isPlainObject(rawState.scopes) ? rawState.scopes : {};
          RUNTIME_RECORDS_VIEW_SCOPES = toSerializable(incomingScopes) || {};
          var activeView = isPlainObject(rawState.active_view) ? rawState.active_view : {};
          var entityFilter = normalizeIdentity(activeView.entity_filter);
          applyRuntimeViewScope(entityFilter);
          RUNTIME_RECORDS_VIEW.search = String(
            activeView.search != null ? activeView.search : RUNTIME_RECORDS_VIEW.search
          );
          RUNTIME_RECORDS_VIEW.status_filter = String(
            activeView.status_filter != null
              ? activeView.status_filter
              : RUNTIME_RECORDS_VIEW.status_filter
          );
          RUNTIME_RECORDS_VIEW.category_filter = String(
            activeView.category_filter != null
              ? activeView.category_filter
              : RUNTIME_RECORDS_VIEW.category_filter
          );
          RUNTIME_RECORDS_VIEW.date_from = String(
            activeView.date_from != null ? activeView.date_from : RUNTIME_RECORDS_VIEW.date_from
          );
          RUNTIME_RECORDS_VIEW.date_to = String(
            activeView.date_to != null ? activeView.date_to : RUNTIME_RECORDS_VIEW.date_to
          );
          RUNTIME_RECORDS_VIEW.page = Math.max(
            1,
            parseInt(
              String(activeView.page != null ? activeView.page : RUNTIME_RECORDS_VIEW.page),
              10
            ) || 1
          );
          RUNTIME_RECORDS_VIEW.page_size = Math.max(
            1,
            parseInt(
              String(
                activeView.page_size != null
                  ? activeView.page_size
                  : RUNTIME_RECORDS_VIEW.page_size
              ),
              10
            ) || 8
          );
          RUNTIME_RECORDS_VIEW.sort_by = String(
            activeView.sort_by != null ? activeView.sort_by : RUNTIME_RECORDS_VIEW.sort_by
          );
          RUNTIME_RECORDS_VIEW.sort_dir =
            String(
              activeView.sort_dir != null ? activeView.sort_dir : RUNTIME_RECORDS_VIEW.sort_dir
            ) === 'desc'
              ? 'desc'
              : 'asc';
          saveRuntimeViewScope();
          RUNTIME_RECORDS_VIEW_JUST_RESTORED = true;
        }

        function extractEntityRecordsFromState(state, entityType) {
          if (!isPlainObject(state) || !entityType) {
            return [];
          }
          var byEntities =
            isPlainObject(state.entities) && Array.isArray(state.entities[entityType])
              ? state.entities[entityType]
              : null;
          if (Array.isArray(byEntities)) {
            return byEntities;
          }
          var byTopLevel = Array.isArray(state[entityType]) ? state[entityType] : null;
          return Array.isArray(byTopLevel) ? byTopLevel : [];
        }

        function collectRuntimeRecordsFromState(state) {
          if (!isPlainObject(state)) {
            return [];
          }
          var entities = isPlainObject(state.entities) ? state.entities : {};
          var rows = [];
          Object.keys(entities).forEach(function (entityType) {
            var records = extractEntityRecordsFromState(state, entityType);
            records.forEach(function (record) {
              if (!isPlainObject(record)) {
                return;
              }
              var nestedData = isPlainObject(record.data) ? record.data : {};
              rows.push({
                entity_type: entityType,
                record_id: normalizeIdentity(record.record_id || record.id || nestedData.record_id || nestedData.id),
                external_id: normalizeIdentity(record.external_id || nestedData.external_id),
                data: extractPersistableRecordData(record) || {},
              });
            });
          });
          return rows;
        }

        function escapeHtml(value) {
          return String(value == null ? '' : value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
        }

        function resolveRecordEditorPayload(row) {
          if (!isPlainObject(row)) {
            return {};
          }
          var payload = isPlainObject(row.data) ? row.data : {};
          if (String(row.entity_type || '').trim().toLowerCase() === 'screen_state') {
            var state = isPlainObject(payload.state) ? payload.state : {};
            if (isPlainObject(state.field_values)) {
              return state.field_values;
            }
            return state;
          }
          return payload;
        }

        function parseDateFilterValue(value) {
          if (value == null || value === '') {
            return NaN;
          }
          var parsed = new Date(String(value));
          var timeValue = parsed.getTime();
          return Number.isNaN(timeValue) ? NaN : timeValue;
        }

        function formatDateForInput(date) {
          if (!(date instanceof Date)) {
            return '';
          }
          var year = String(date.getFullYear());
          var month = String(date.getMonth() + 1).padStart(2, '0');
          var day = String(date.getDate()).padStart(2, '0');
          return year + '-' + month + '-' + day;
        }

        function shiftDays(date, days) {
          var cloned = new Date(date.getTime());
          cloned.setDate(cloned.getDate() + days);
          return cloned;
        }

        function applyRuntimeQuickPreset(presetKey) {
          var key = String(presetKey || '').trim();
          if (!key) {
            return false;
          }
          var now = new Date();
          if (key === 'last_7_days') {
            RUNTIME_RECORDS_VIEW.date_to = formatDateForInput(now);
            RUNTIME_RECORDS_VIEW.date_from = formatDateForInput(shiftDays(now, -6));
          } else if (key === 'last_30_days') {
            RUNTIME_RECORDS_VIEW.date_to = formatDateForInput(now);
            RUNTIME_RECORDS_VIEW.date_from = formatDateForInput(shiftDays(now, -29));
          } else if (key === 'clear_period') {
            RUNTIME_RECORDS_VIEW.date_from = '';
            RUNTIME_RECORDS_VIEW.date_to = '';
          } else {
            return false;
          }
          RUNTIME_RECORDS_VIEW.page = 1;
          return true;
        }

        function countActiveRuntimeFilters() {
          var count = 0;
          if (String(RUNTIME_RECORDS_VIEW.entity_filter || '').trim()) count += 1;
          if (String(RUNTIME_RECORDS_VIEW.search || '').trim()) count += 1;
          if (String(RUNTIME_RECORDS_VIEW.status_filter || '').trim()) count += 1;
          if (String(RUNTIME_RECORDS_VIEW.category_filter || '').trim()) count += 1;
          if (String(RUNTIME_RECORDS_VIEW.date_from || '').trim()) count += 1;
          if (String(RUNTIME_RECORDS_VIEW.date_to || '').trim()) count += 1;
          return count;
        }

        function clearRuntimeFilters() {
          RUNTIME_RECORDS_VIEW.entity_filter = '';
          RUNTIME_RECORDS_VIEW.search = '';
          RUNTIME_RECORDS_VIEW.status_filter = '';
          RUNTIME_RECORDS_VIEW.category_filter = '';
          RUNTIME_RECORDS_VIEW.date_from = '';
          RUNTIME_RECORDS_VIEW.date_to = '';
          RUNTIME_RECORDS_VIEW.page = 1;
        }

        function hasActiveDateRangeFilter() {
          return (
            String(RUNTIME_RECORDS_VIEW.date_from || '').trim() !== '' ||
            String(RUNTIME_RECORDS_VIEW.date_to || '').trim() !== ''
          );
        }

        function countRowsWithoutValidDate(rows, dateKey) {
          var key = String(dateKey || '').trim();
          if (!key) {
            return 0;
          }
          var count = 0;
          rows.forEach(function (row) {
            var rowData = isPlainObject(row && row.data) ? row.data : {};
            if (Number.isNaN(parseDateFilterValue(rowData[key]))) {
              count += 1;
            }
          });
          return count;
        }

        function buildRuntimeFilterMeta(rows) {
          var statusKeys = ['status', 'state', 'situation'];
          var categoryKeys = ['category', 'type', 'segment', 'kind'];
          var dateKeys = [
            'date',
            'created_at',
            'updated_at',
            'timestamp',
            'createdAt',
            'updatedAt',
          ];
          var statuses = [];
          var categories = [];
          var entities = [];
          var dateKeyCounts = {};
          rows.forEach(function (row) {
            var entityType = normalizeIdentity(row && row.entity_type);
            if (entityType && entities.indexOf(entityType) < 0) {
              entities.push(entityType);
            }
            var data = isPlainObject(row && row.data) ? row.data : {};
            statusKeys.forEach(function (key) {
              var value = normalizeIdentity(data[key]);
              if (value && statuses.indexOf(value) < 0) {
                statuses.push(value);
              }
            });
            categoryKeys.forEach(function (key) {
              var value = normalizeIdentity(data[key]);
              if (value && categories.indexOf(value) < 0) {
                categories.push(value);
              }
            });
            dateKeys.forEach(function (key) {
              var rawValue = data[key];
              if (rawValue == null || rawValue === '') {
                return;
              }
              if (!Number.isNaN(parseDateFilterValue(rawValue))) {
                dateKeyCounts[key] = (dateKeyCounts[key] || 0) + 1;
              }
            });
          });

          var selectedDateKey = '';
          var selectedDateCount = 0;
          dateKeys.forEach(function (key) {
            var count = dateKeyCounts[key] || 0;
            if (count > selectedDateCount) {
              selectedDateCount = count;
              selectedDateKey = key;
            }
          });

          statuses.sort();
          categories.sort();
          entities.sort();

          return {
            entities: entities,
            statuses: statuses,
            categories: categories,
            date_key: selectedDateKey,
          };
        }

        function filterRuntimeRows(rows, filterMeta) {
          var entityFilter = String(RUNTIME_RECORDS_VIEW.entity_filter || '').trim().toLowerCase();
          var search = String(RUNTIME_RECORDS_VIEW.search || '').trim().toLowerCase();
          var statusFilter = String(RUNTIME_RECORDS_VIEW.status_filter || '').trim().toLowerCase();
          var categoryFilter = String(RUNTIME_RECORDS_VIEW.category_filter || '').trim().toLowerCase();
          var dateFromRaw = String(RUNTIME_RECORDS_VIEW.date_from || '').trim();
          var dateToRaw = String(RUNTIME_RECORDS_VIEW.date_to || '').trim();
          var dateKey = String((filterMeta && filterMeta.date_key) || '').trim();
          var dateFrom = dateFromRaw ? parseDateFilterValue(dateFromRaw + 'T00:00:00') : NaN;
          var dateTo = dateToRaw ? parseDateFilterValue(dateToRaw + 'T23:59:59') : NaN;
          return rows.filter(function (row) {
            var entity = String(row.entity_type || '').trim().toLowerCase();
            if (entityFilter && entity !== entityFilter) {
              return false;
            }
            var rowData = isPlainObject(row && row.data) ? row.data : {};
            var statusValue = normalizeIdentity(
              rowData.status || rowData.state || rowData.situation
            ).toLowerCase();
            if (statusFilter && statusValue !== statusFilter) {
              return false;
            }
            var categoryValue = normalizeIdentity(
              rowData.category || rowData.type || rowData.segment || rowData.kind
            ).toLowerCase();
            if (categoryFilter && categoryValue !== categoryFilter) {
              return false;
            }
            if (dateKey && (!Number.isNaN(dateFrom) || !Number.isNaN(dateTo))) {
              var rowDate = parseDateFilterValue(rowData[dateKey]);
              if (Number.isNaN(rowDate)) {
                return false;
              }
              if (!Number.isNaN(dateFrom) && rowDate < dateFrom) {
                return false;
              }
              if (!Number.isNaN(dateTo) && rowDate > dateTo) {
                return false;
              }
            }
            if (!search) {
              return true;
            }
            var recordId = String(row.record_id || '').toLowerCase();
            var externalId = String(row.external_id || '').toLowerCase();
            var payload = JSON.stringify(rowData).toLowerCase();
            return (
              entity.indexOf(search) >= 0 ||
              recordId.indexOf(search) >= 0 ||
              externalId.indexOf(search) >= 0 ||
              payload.indexOf(search) >= 0
            );
          });
        }

        function buildRuntimeFilterDiagnostics(rows, filterMeta) {
          var entityFilter = String(RUNTIME_RECORDS_VIEW.entity_filter || '').trim().toLowerCase();
          var search = String(RUNTIME_RECORDS_VIEW.search || '').trim().toLowerCase();
          var statusFilter = String(RUNTIME_RECORDS_VIEW.status_filter || '').trim().toLowerCase();
          var categoryFilter = String(RUNTIME_RECORDS_VIEW.category_filter || '').trim().toLowerCase();
          var dateFromRaw = String(RUNTIME_RECORDS_VIEW.date_from || '').trim();
          var dateToRaw = String(RUNTIME_RECORDS_VIEW.date_to || '').trim();
          var dateKey = String((filterMeta && filterMeta.date_key) || '').trim();
          var dateFrom = dateFromRaw ? parseDateFilterValue(dateFromRaw + 'T00:00:00') : NaN;
          var dateTo = dateToRaw ? parseDateFilterValue(dateToRaw + 'T23:59:59') : NaN;
          var hasDateFilter = dateKey && (!Number.isNaN(dateFrom) || !Number.isNaN(dateTo));
          var diagnostics = {
            by_entity: 0,
            by_status: 0,
            by_category: 0,
            by_date_invalid: 0,
            by_date_range: 0,
            by_search: 0,
          };
          rows.forEach(function (row) {
            var entity = String(row.entity_type || '').trim().toLowerCase();
            var rowData = isPlainObject(row && row.data) ? row.data : {};
            if (entityFilter && entity !== entityFilter) {
              diagnostics.by_entity += 1;
              return;
            }
            var statusValue = normalizeIdentity(
              rowData.status || rowData.state || rowData.situation
            ).toLowerCase();
            if (statusFilter && statusValue !== statusFilter) {
              diagnostics.by_status += 1;
              return;
            }
            var categoryValue = normalizeIdentity(
              rowData.category || rowData.type || rowData.segment || rowData.kind
            ).toLowerCase();
            if (categoryFilter && categoryValue !== categoryFilter) {
              diagnostics.by_category += 1;
              return;
            }
            if (hasDateFilter) {
              var rowDate = parseDateFilterValue(rowData[dateKey]);
              if (Number.isNaN(rowDate)) {
                diagnostics.by_date_invalid += 1;
                return;
              }
              if (!Number.isNaN(dateFrom) && rowDate < dateFrom) {
                diagnostics.by_date_range += 1;
                return;
              }
              if (!Number.isNaN(dateTo) && rowDate > dateTo) {
                diagnostics.by_date_range += 1;
                return;
              }
            }
            if (search) {
              var recordId = String(row.record_id || '').toLowerCase();
              var externalId = String(row.external_id || '').toLowerCase();
              var payload = JSON.stringify(rowData).toLowerCase();
              if (
                entity.indexOf(search) < 0 &&
                recordId.indexOf(search) < 0 &&
                externalId.indexOf(search) < 0 &&
                payload.indexOf(search) < 0
              ) {
                diagnostics.by_search += 1;
              }
            }
          });
          return diagnostics;
        }

        function buildRuntimeDistribution(rows, candidateKeys) {
          var keys = Array.isArray(candidateKeys) ? candidateKeys : [];
          var counts = {};
          rows.forEach(function (row) {
            var rowData = isPlainObject(row && row.data) ? row.data : {};
            var value = '';
            for (var i = 0; i < keys.length; i += 1) {
              var key = keys[i];
              value = normalizeIdentity(rowData[key]);
              if (value) {
                break;
              }
            }
            if (!value) {
              return;
            }
            counts[value] = (counts[value] || 0) + 1;
          });
          return Object.keys(counts)
            .map(function (label) {
              return { label: label, count: counts[label] };
            })
            .sort(function (left, right) {
              if (right.count !== left.count) {
                return right.count - left.count;
              }
              return left.label.localeCompare(right.label);
            });
        }

        function buildRuntimeDashboardSection(rows, filteredRows, filterMeta, hiddenByInvalidDateCount) {
          var totalRows = rows.length;
          var visibleRows = filteredRows.length;
          var hiddenRows = Math.max(0, totalRows - visibleRows);
          var entitiesCount = filterMeta && Array.isArray(filterMeta.entities)
            ? filterMeta.entities.length
            : 0;
          var statusDistribution = buildRuntimeDistribution(filteredRows, ['status', 'state', 'situation']);
          var categoryDistribution = buildRuntimeDistribution(filteredRows, ['category', 'type', 'segment', 'kind']);
          var maxStatusCount = statusDistribution.length ? statusDistribution[0].count : 0;
          var maxCategoryCount = categoryDistribution.length ? categoryDistribution[0].count : 0;

          function renderMetricCard(title, value, subtitle) {
            return (
              '<div style="min-width:120px;flex:1;border:1px solid #e2e8f0;border-radius:8px;background:#fff;padding:8px;">' +
                '<div style="font-size:11px;color:#64748b;">' + escapeHtml(title) + '</div>' +
                '<div style="margin-top:4px;font-size:18px;line-height:1.1;color:#0f172a;font-weight:600;">' + escapeHtml(String(value)) + '</div>' +
                '<div style="margin-top:4px;font-size:11px;color:#94a3b8;">' + escapeHtml(subtitle) + '</div>' +
              '</div>'
            );
          }

          function renderDistribution(title, items, maxCount, attrName) {
            if (!items.length) {
              return (
                '<div style="flex:1;min-width:220px;border:1px solid #e2e8f0;border-radius:8px;background:#fff;padding:8px;">' +
                  '<div style="font-size:12px;color:#334155;font-weight:600;margin-bottom:6px;">' + escapeHtml(title) + '</div>' +
                  '<div style="font-size:11px;color:#94a3b8;">Sem dados para exibir.</div>' +
                '</div>'
              );
            }
            var topItems = items.slice(0, 6);
            return (
              '<div style="flex:1;min-width:220px;border:1px solid #e2e8f0;border-radius:8px;background:#fff;padding:8px;">' +
                '<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;">' +
                  '<div style="font-size:12px;color:#334155;font-weight:600;">' + escapeHtml(title) + '</div>' +
                  '<div style="font-size:10px;color:#94a3b8;">Top ' + String(topItems.length) + '</div>' +
                '</div>' +
                topItems
                  .map(function (item) {
                    var ratio = maxCount > 0 ? Math.max(8, Math.round((item.count / maxCount) * 100)) : 8;
                    return (
                      '<button type="button" ' +
                        attrName + '="' + escapeHtml(item.label) + '" ' +
                        'style="all:unset;display:block;width:100%;cursor:pointer;margin-bottom:6px;">' +
                        '<div style="display:flex;justify-content:space-between;gap:8px;font-size:11px;color:#334155;">' +
                          '<span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">' + escapeHtml(item.label) + '</span>' +
                          '<span>' + String(item.count) + '</span>' +
                        '</div>' +
                        '<div style="margin-top:4px;height:6px;background:#f1f5f9;border-radius:999px;overflow:hidden;">' +
                          '<div style="height:100%;width:' + String(ratio) + '%;background:#93c5fd;"></div>' +
                        '</div>' +
                      '</button>'
                    );
                  })
                  .join('') +
              '</div>'
            );
          }

          var quickActions =
            '<div style="display:flex;flex-wrap:wrap;gap:6px;">' +
              (String(RUNTIME_RECORDS_VIEW.entity_filter || '').trim()
                ? '<button type="button" data-devspott-dashboard-clear-entity="1" style="height:24px;padding:0 8px;border:1px solid #cbd5e1;border-radius:6px;background:#fff;color:#334155;cursor:pointer;font-size:11px;">Limpar entidade</button>'
                : '') +
              (String(RUNTIME_RECORDS_VIEW.status_filter || '').trim()
                ? '<button type="button" data-devspott-dashboard-clear-status="1" style="height:24px;padding:0 8px;border:1px solid #cbd5e1;border-radius:6px;background:#fff;color:#334155;cursor:pointer;font-size:11px;">Limpar status</button>'
                : '') +
              (String(RUNTIME_RECORDS_VIEW.category_filter || '').trim()
                ? '<button type="button" data-devspott-dashboard-clear-category="1" style="height:24px;padding:0 8px;border:1px solid #cbd5e1;border-radius:6px;background:#fff;color:#334155;cursor:pointer;font-size:11px;">Limpar categoria</button>'
                : '') +
            '</div>';

          return (
            '<section style="margin-bottom:8px;border:1px solid #e2e8f0;border-radius:10px;background:#f8fafc;padding:10px;">' +
              '<div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:8px;">' +
                '<strong style="font-size:12px;color:#0f172a;">Painel analítico</strong>' +
                '<span style="font-size:11px;color:#64748b;">Filtros aplicados em tempo real</span>' +
              '</div>' +
              '<div style="display:flex;flex-wrap:wrap;gap:8px;">' +
                renderMetricCard('Registros visíveis', visibleRows, 'após filtros') +
                renderMetricCard('Registros totais', totalRows, 'antes de filtros') +
                renderMetricCard('Ocultados por filtros', hiddenRows, 'visão atual') +
                renderMetricCard('Entidades ativas', entitiesCount, 'no estado runtime') +
              '</div>' +
              '<div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:8px;">' +
                renderDistribution('Status (clique para filtrar)', statusDistribution, maxStatusCount, 'data-devspott-dashboard-status') +
                renderDistribution('Categoria (clique para filtrar)', categoryDistribution, maxCategoryCount, 'data-devspott-dashboard-category') +
              '</div>' +
              '<div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:8px;">' +
                '<div style="font-size:11px;color:#64748b;">' +
                  (hiddenByInvalidDateCount > 0
                    ? String(hiddenByInvalidDateCount) + ' sem data válida no período.'
                    : 'Sem inconsistências de data no período atual.') +
                '</div>' +
                quickActions +
              '</div>' +
            '</section>'
          );
        }

        function sortRuntimeRows(rows) {
          var sortBy = String(RUNTIME_RECORDS_VIEW.sort_by || 'entity_type');
          var sortDir = String(RUNTIME_RECORDS_VIEW.sort_dir || 'asc') === 'desc' ? -1 : 1;
          var sortable = rows.slice();
          sortable.sort(function (left, right) {
            var leftValue = String((left && left[sortBy]) || '').toLowerCase();
            var rightValue = String((right && right[sortBy]) || '').toLowerCase();
            if (leftValue < rightValue) return -1 * sortDir;
            if (leftValue > rightValue) return 1 * sortDir;
            return 0;
          });
          return sortable;
        }

        function postRuntimeRecordsPanelToParent(html, visible) {
          try {
            window.parent.postMessage(
              {
                source: CHANNEL_SOURCE,
                type: 'runtime_records_panel',
                messageId: MESSAGE_ID,
                html: html || '',
                visible: !!visible,
              },
              '*'
            );
          } catch (e) {}
        }

        function ensureRuntimeRecordsTableHost() {
          var host = document.getElementById('devspott-runtime-records-table');
          if (host) {
            return host;
          }
          host = document.createElement('section');
          host.id = 'devspott-runtime-records-table';
          host.setAttribute('data-devspott-runtime-records', '1');
          host.setAttribute('aria-hidden', 'true');
          host.style.cssText =
            'display:none;margin:0;padding:0;border:0;clip:rect(0,0,0,0);position:absolute;width:1px;height:1px;overflow:hidden;';
          document.body.appendChild(host);
          return host;
        }

        function ensureRuntimeAlertContainer() {
          var container = document.getElementById('devspott-runtime-alert-container');
          if (!container) {
            container = document.createElement('div');
            container.id = 'devspott-runtime-alert-container';
            container.style.cssText =
              'position:fixed;top:16px;right:16px;z-index:99999;display:flex;flex-direction:column;gap:8px;pointer-events:none;';
            document.body.appendChild(container);
          }
          return container;
        }

        function confirmDeleteWithUndo(row) {
          var entityType = normalizeIdentity(row && row.entity_type);
          var recordId = normalizeIdentity(row && row.record_id);
          var externalId = normalizeIdentity(row && row.external_id);
          if (!recordId && !externalId) {
            return;
          }
          var deleteKey = [entityType, recordId || '-', externalId || '-'].join(':');
          if (!window.__devspottPendingDeletes) {
            window.__devspottPendingDeletes = {};
          }
          var pending = window.__devspottPendingDeletes;
          if (pending[deleteKey] && pending[deleteKey].timer) {
            clearTimeout(pending[deleteKey].timer);
          }

          var container = ensureRuntimeAlertContainer();
          var toastId = 'delete-confirm-' + deleteKey.replace(/[^a-zA-Z0-9_-]/g, '_');
          var previous = container.querySelector('[data-devspott-alert-key="' + toastId + '"]');
          if (previous && previous.parentNode) {
            previous.parentNode.removeChild(previous);
          }
          var toast = document.createElement('div');
          toast.setAttribute('data-devspott-alert-key', toastId);
          toast.style.cssText =
            'pointer-events:auto;padding:10px 12px;border-radius:8px;font-size:13px;color:#7f1d1d;' +
            'box-shadow:0 2px 8px rgba(0,0,0,.18);background:#fef2f2;border:1px solid #fca5a5;' +
            'display:flex;align-items:center;gap:10px;max-width:380px;';
          var label = document.createElement('span');
          label.textContent = 'Exclusão agendada. Desfazer?';
          var undo = document.createElement('button');
          undo.type = 'button';
          undo.textContent = 'Desfazer';
          undo.style.cssText =
            'padding:4px 10px;border:1px solid #fecaca;border-radius:6px;background:#fff;color:#7f1d1d;cursor:pointer;';
          toast.appendChild(label);
          toast.appendChild(undo);
          container.appendChild(toast);

          var timer = setTimeout(function () {
            triggerDeleteRecord(row);
            if (toast.parentNode) {
              toast.parentNode.removeChild(toast);
            }
            delete pending[deleteKey];
          }, 3500);

          pending[deleteKey] = { timer: timer };
          undo.addEventListener('click', function () {
            if (pending[deleteKey] && pending[deleteKey].timer) {
              clearTimeout(pending[deleteKey].timer);
            }
            delete pending[deleteKey];
            if (toast.parentNode) {
              toast.parentNode.removeChild(toast);
            }
          });
        }

        function resolveRuntimeRecordKey(row) {
          if (!isPlainObject(row)) {
            return '';
          }
          var entityType = normalizeIdentity(row.entity_type) || '-';
          var recordId = normalizeIdentity(row.record_id) || '-';
          var externalId = normalizeIdentity(row.external_id) || '-';
          return [entityType, recordId, externalId].join(':');
        }

        function openRecordAtIndex(index) {
          if (typeof index !== 'number' || index < 0 || index >= RUNTIME_RECORDS_CACHE.length) {
            return false;
          }
          var selected = RUNTIME_RECORDS_CACHE[index];
          var payload = resolveRecordEditorPayload(selected);
          applyValues(payload);
          postValues();
          return true;
        }

        function renderRuntimeRecordsTable(state) {
          var host = ensureRuntimeRecordsTableHost();
          var rows = collectRuntimeRecordsFromState(state);
          RUNTIME_RECORDS_CACHE = rows;
          if (rows.length <= 1) {
            host.style.display = 'none';
            host.innerHTML = '';
            postRuntimeRecordsPanelToParent('', false);
            if (rows.length === 1) {
              var singleRecordKey = resolveRuntimeRecordKey(rows[0]);
              if (window.__devspottAutoLoadedRecordKey !== singleRecordKey) {
                window.__devspottAutoLoadedRecordKey = singleRecordKey;
                openRecordAtIndex(0);
              }
            } else {
              window.__devspottAutoLoadedRecordKey = null;
            }
            return;
          }
          host.style.display = 'none';
          window.__devspottAutoLoadedRecordKey = null;
          var filterMeta = buildRuntimeFilterMeta(rows);
          if (
            RUNTIME_RECORDS_VIEW.entity_filter &&
            filterMeta.entities.indexOf(String(RUNTIME_RECORDS_VIEW.entity_filter)) < 0
          ) {
            applyRuntimeViewScope('');
          }
          if (
            RUNTIME_RECORDS_VIEW.status_filter &&
            filterMeta.statuses.indexOf(String(RUNTIME_RECORDS_VIEW.status_filter)) < 0
          ) {
            RUNTIME_RECORDS_VIEW.status_filter = '';
          }
          if (
            RUNTIME_RECORDS_VIEW.category_filter &&
            filterMeta.categories.indexOf(String(RUNTIME_RECORDS_VIEW.category_filter)) < 0
          ) {
            RUNTIME_RECORDS_VIEW.category_filter = '';
          }
          var hiddenByInvalidDateCount =
            hasActiveDateRangeFilter() && filterMeta.date_key
              ? countRowsWithoutValidDate(rows, filterMeta.date_key)
              : 0;
          var activeFiltersCount = countActiveRuntimeFilters();
          var filterDiagnostics = buildRuntimeFilterDiagnostics(rows, filterMeta);
          var filteredRows = sortRuntimeRows(filterRuntimeRows(rows, filterMeta));
          var pageSize = Math.max(1, parseInt(String(RUNTIME_RECORDS_VIEW.page_size || 8), 10) || 8);
          var totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
          var currentPage = Math.min(
            Math.max(1, parseInt(String(RUNTIME_RECORDS_VIEW.page || 1), 10) || 1),
            totalPages
          );
          RUNTIME_RECORDS_VIEW.page = currentPage;
          var startIndex = (currentPage - 1) * pageSize;
          var pageRows = filteredRows.slice(startIndex, startIndex + pageSize);
          if (!rows.length) {
            host.innerHTML =
              '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">' +
                '<strong style="font-size:13px;color:#0f172a;">Registros salvos</strong>' +
                '<span style="font-size:12px;color:#64748b;">0</span>' +
              '</div>' +
              '<div style="font-size:12px;color:#64748b;">Nenhum registro salvo para exibir.</div>';
            return;
          }
          var entityTabs =
            '<div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:8px;">' +
              '<button type="button" data-devspott-entity-chip="" ' +
                'style="padding:4px 10px;border:1px solid ' +
                (RUNTIME_RECORDS_VIEW.entity_filter ? '#cbd5e1' : '#0f172a') +
                ';border-radius:999px;background:' +
                (RUNTIME_RECORDS_VIEW.entity_filter ? '#fff' : '#0f172a') +
                ';color:' +
                (RUNTIME_RECORDS_VIEW.entity_filter ? '#334155' : '#fff') +
                ';cursor:pointer;font-size:12px;">Todas</button>' +
              filterMeta.entities
                .map(function (entity) {
                  var isActive = String(RUNTIME_RECORDS_VIEW.entity_filter || '') === entity;
                  return (
                    '<button type="button" data-devspott-entity-chip="' +
                    escapeHtml(entity) +
                    '" style="padding:4px 10px;border:1px solid ' +
                    (isActive ? '#0f172a' : '#cbd5e1') +
                    ';border-radius:999px;background:' +
                    (isActive ? '#0f172a' : '#fff') +
                    ';color:' +
                    (isActive ? '#fff' : '#334155') +
                    ';cursor:pointer;font-size:12px;">' +
                    escapeHtml(entity) +
                    '</button>'
                  );
                })
                .join('') +
            '</div>';
          var filtersBar =
            '<div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:8px;">' +
              '<select data-devspott-records-status-filter="1" style="height:30px;border:1px solid #cbd5e1;border-radius:6px;padding:0 8px;font-size:12px;background:#fff;color:#0f172a;">' +
                '<option value="">Todos status</option>' +
                filterMeta.statuses
                  .map(function (entity) {
                    var selected = String(RUNTIME_RECORDS_VIEW.status_filter || '') === entity ? ' selected' : '';
                    return (
                      '<option value="' + escapeHtml(entity) + '"' + selected + '>' + escapeHtml(entity) + '</option>'
                    );
                  })
                  .join('') +
              '</select>' +
              '<select data-devspott-records-category-filter="1" style="height:30px;border:1px solid #cbd5e1;border-radius:6px;padding:0 8px;font-size:12px;background:#fff;color:#0f172a;">' +
                '<option value="">Todas categorias</option>' +
                filterMeta.categories
                  .map(function (entity) {
                    var selected = String(RUNTIME_RECORDS_VIEW.category_filter || '') === entity ? ' selected' : '';
                    return (
                      '<option value="' + escapeHtml(entity) + '"' + selected + '>' + escapeHtml(entity) + '</option>'
                    );
                  })
                  .join('') +
              '</select>' +
              (filterMeta.date_key
                ? '<input data-devspott-records-date-from="1" type="date" value="' +
                    escapeHtml(RUNTIME_RECORDS_VIEW.date_from || '') +
                    '" style="height:30px;border:1px solid #cbd5e1;border-radius:6px;padding:0 8px;font-size:12px;color:#0f172a;" />' +
                  '<input data-devspott-records-date-to="1" type="date" value="' +
                    escapeHtml(RUNTIME_RECORDS_VIEW.date_to || '') +
                    '" style="height:30px;border:1px solid #cbd5e1;border-radius:6px;padding:0 8px;font-size:12px;color:#0f172a;" />'
                : '') +
              '<input data-devspott-records-search="1" type="text" value="' +
                escapeHtml(RUNTIME_RECORDS_VIEW.search || '') +
                '" placeholder="Buscar em entidade, IDs e dados..." ' +
                'style="height:30px;min-width:260px;flex:1;border:1px solid #cbd5e1;border-radius:6px;padding:0 8px;font-size:12px;color:#0f172a;" />' +
              '<button type="button" data-devspott-clear-filters="1" ' +
                'style="height:30px;padding:0 10px;border:1px solid #cbd5e1;border-radius:6px;background:#fff;color:#334155;cursor:pointer;font-size:12px;">Limpar</button>' +
            '</div>';
          var quickFiltersBar =
            '<div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:-2px;margin-bottom:8px;">' +
              '<button type="button" data-devspott-filter-preset="last_7_days" style="padding:3px 8px;border:1px solid #cbd5e1;border-radius:999px;background:#fff;color:#334155;cursor:pointer;font-size:11px;">7 dias</button>' +
              '<button type="button" data-devspott-filter-preset="last_30_days" style="padding:3px 8px;border:1px solid #cbd5e1;border-radius:999px;background:#fff;color:#334155;cursor:pointer;font-size:11px;">30 dias</button>' +
              '<button type="button" data-devspott-filter-preset="clear_period" style="padding:3px 8px;border:1px solid #cbd5e1;border-radius:999px;background:#fff;color:#334155;cursor:pointer;font-size:11px;">Limpar período</button>' +
            '</div>';
          var dateFilterHint =
            hiddenByInvalidDateCount > 0
              ? '<div style="margin-top:-2px;margin-bottom:8px;font-size:11px;color:#92400e;background:#fffbeb;border:1px solid #fde68a;border-radius:6px;padding:6px 8px;">' +
                  String(hiddenByInvalidDateCount) +
                  ' registro(s) não apareceram porque não têm data válida para o período selecionado.' +
                '</div>'
              : '';
          var dashboardSection = buildRuntimeDashboardSection(
            rows,
            filteredRows,
            filterMeta,
            hiddenByInvalidDateCount
          );
          var emptyReasonsHint = '';
          if (!filteredRows.length && rows.length && activeFiltersCount > 0) {
            var reasons = [];
            if (filterDiagnostics.by_entity > 0) {
              reasons.push('entidade: ' + String(filterDiagnostics.by_entity));
            }
            if (filterDiagnostics.by_status > 0) {
              reasons.push('status: ' + String(filterDiagnostics.by_status));
            }
            if (filterDiagnostics.by_category > 0) {
              reasons.push('categoria: ' + String(filterDiagnostics.by_category));
            }
            if (filterDiagnostics.by_date_invalid > 0) {
              reasons.push('sem data válida: ' + String(filterDiagnostics.by_date_invalid));
            }
            if (filterDiagnostics.by_date_range > 0) {
              reasons.push('fora do período: ' + String(filterDiagnostics.by_date_range));
            }
            if (filterDiagnostics.by_search > 0) {
              reasons.push('busca textual: ' + String(filterDiagnostics.by_search));
            }
            if (reasons.length > 0) {
              var recoveryActions = [];
              if (filterDiagnostics.by_search > 0 && String(RUNTIME_RECORDS_VIEW.search || '').trim()) {
                recoveryActions.push(
                  '<button type="button" data-devspott-empty-action="clear_search" style="height:24px;padding:0 8px;border:1px solid #cbd5e1;border-radius:6px;background:#fff;color:#334155;cursor:pointer;font-size:11px;">Limpar busca</button>'
                );
              }
              if (
                (filterDiagnostics.by_date_invalid > 0 || filterDiagnostics.by_date_range > 0) &&
                hasActiveDateRangeFilter()
              ) {
                recoveryActions.push(
                  '<button type="button" data-devspott-empty-action="clear_period" style="height:24px;padding:0 8px;border:1px solid #cbd5e1;border-radius:6px;background:#fff;color:#334155;cursor:pointer;font-size:11px;">Limpar período</button>'
                );
              }
              if (activeFiltersCount > 1) {
                recoveryActions.push(
                  '<button type="button" data-devspott-empty-action="clear_all" style="height:24px;padding:0 8px;border:1px solid #cbd5e1;border-radius:6px;background:#fff;color:#334155;cursor:pointer;font-size:11px;">Limpar tudo</button>'
                );
              }
              emptyReasonsHint =
                '<div style="margin-top:-2px;margin-bottom:8px;font-size:11px;color:#1f2937;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:6px 8px;">' +
                  'Nenhum registro com os filtros atuais. Motivos prováveis: ' +
                  escapeHtml(reasons.join(' | ')) +
                  (recoveryActions.length > 0
                    ? '<div style="margin-top:6px;display:flex;flex-wrap:wrap;gap:6px;">' +
                        recoveryActions.join('') +
                      '</div>'
                    : '') +
                '</div>';
            }
          }
          var header =
            (function () {
              return (
            '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">' +
              '<div style="display:flex;align-items:center;gap:8px;">' +
                '<strong style="font-size:13px;color:#0f172a;">Registros salvos</strong>' +
                (RUNTIME_RECORDS_VIEW_JUST_RESTORED
                  ? '<span style="font-size:11px;color:#065f46;background:#ecfdf5;border:1px solid #a7f3d0;border-radius:999px;padding:2px 8px;">Contexto restaurado</span>'
                  : '') +
                (activeFiltersCount > 0
                  ? '<span style="font-size:11px;color:#1d4ed8;background:#eff6ff;border:1px solid #bfdbfe;border-radius:999px;padding:2px 8px;">Filtros ativos: ' + String(activeFiltersCount) + '</span>'
                  : '') +
              '</div>' +
              '<div style="display:flex;align-items:center;gap:8px;">' +
                '<span style="font-size:12px;color:#64748b;">' + String(filteredRows.length) + ' de ' + String(rows.length) + '</span>' +
                (activeFiltersCount > 0
                  ? '<button type="button" data-devspott-clear-all-filters="1" style="height:26px;padding:0 10px;border:1px solid #cbd5e1;border-radius:6px;background:#fff;color:#334155;cursor:pointer;font-size:12px;">Limpar tudo</button>'
                  : '') +
              '</div>' +
            '</div>'
              );
            })();
          function sortLabel(key, title) {
            var active = String(RUNTIME_RECORDS_VIEW.sort_by || '') === key;
            var arrow = active
              ? (String(RUNTIME_RECORDS_VIEW.sort_dir || 'asc') === 'desc' ? ' ▼' : ' ▲')
              : '';
            return (
              '<button type="button" data-devspott-sort-key="' + key + '" ' +
              'style="all:unset;cursor:pointer;color:#0f172a;font-weight:600;">' +
              title + arrow +
              '</button>'
            );
          }

          var table =
            '<div style="overflow:auto;max-height:240px;border:1px solid #e2e8f0;border-radius:8px;">' +
              '<table style="width:100%;border-collapse:collapse;font-size:12px;">' +
                '<thead style="position:sticky;top:0;background:#f8fafc;">' +
                  '<tr>' +
                    '<th style="text-align:left;padding:8px;border-bottom:1px solid #e2e8f0;">' + sortLabel('entity_type', 'Entidade') + '</th>' +
                    '<th style="text-align:left;padding:8px;border-bottom:1px solid #e2e8f0;">' + sortLabel('record_id', 'Record ID') + '</th>' +
                    '<th style="text-align:left;padding:8px;border-bottom:1px solid #e2e8f0;">' + sortLabel('external_id', 'External ID') + '</th>' +
                    '<th style="text-align:left;padding:8px;border-bottom:1px solid #e2e8f0;">Ações</th>' +
                  '</tr>' +
                '</thead><tbody>';
          if (!pageRows.length) {
            table +=
              '<tr><td colspan="4" style="padding:12px;color:#64748b;border-bottom:1px solid #f1f5f9;">Nenhum registro encontrado para os filtros atuais.</td></tr>';
          }
          pageRows.forEach(function (row, pageIndex) {
            var absoluteIndex = startIndex + pageIndex;
            table +=
              '<tr>' +
                '<td style="padding:8px;border-bottom:1px solid #f1f5f9;color:#0f172a;">' + escapeHtml(row.entity_type || '') + '</td>' +
                '<td style="padding:8px;border-bottom:1px solid #f1f5f9;color:#334155;">' + escapeHtml(row.record_id || '-') + '</td>' +
                '<td style="padding:8px;border-bottom:1px solid #f1f5f9;color:#334155;">' + escapeHtml(row.external_id || '-') + '</td>' +
                '<td style="padding:8px;border-bottom:1px solid #f1f5f9;display:flex;gap:6px;flex-wrap:wrap;">' +
                  '<button type="button" data-devspott-load-record-index="' + String(absoluteIndex) + '" style="padding:4px 8px;border:1px solid #cbd5e1;border-radius:6px;background:#fff;color:#0f172a;cursor:pointer;">Editar</button>' +
                  '<button type="button" data-devspott-duplicate-record-index="' + String(absoluteIndex) + '" style="padding:4px 8px;border:1px solid #fdba74;border-radius:6px;background:#fff7ed;color:#9a3412;cursor:pointer;">Duplicar</button>' +
                  '<button type="button" data-devspott-delete-record-index="' + String(absoluteIndex) + '" style="padding:4px 8px;border:1px solid #fca5a5;border-radius:6px;background:#fef2f2;color:#b91c1c;cursor:pointer;">Excluir</button>' +
                '</td>' +
              '</tr>';
          });
          table += '</tbody></table></div>';
          var pagination =
            '<div style="display:flex;justify-content:space-between;align-items:center;margin-top:8px;font-size:12px;color:#64748b;">' +
              '<div style="display:flex;align-items:center;gap:8px;">' +
                '<span>Página ' + String(currentPage) + ' de ' + String(totalPages) + '</span>' +
                '<span style="color:#94a3b8;">Atalhos: /, Esc, Alt+←/→</span>' +
              '</div>' +
              '<div style="display:flex;gap:6px;align-items:center;">' +
                '<label style="display:flex;align-items:center;gap:4px;color:#64748b;">' +
                  '<span>Por página</span>' +
                  '<select data-devspott-page-size="1" style="height:26px;border:1px solid #cbd5e1;border-radius:6px;padding:0 6px;background:#fff;color:#0f172a;font-size:12px;">' +
                    '<option value="8"' + (pageSize === 8 ? ' selected' : '') + '>8</option>' +
                    '<option value="15"' + (pageSize === 15 ? ' selected' : '') + '>15</option>' +
                    '<option value="30"' + (pageSize === 30 ? ' selected' : '') + '>30</option>' +
                    '<option value="50"' + (pageSize === 50 ? ' selected' : '') + '>50</option>' +
                  '</select>' +
                '</label>' +
                '<button type="button" data-devspott-records-prev-page="1" ' +
                  (currentPage <= 1 ? 'disabled ' : '') +
                  'style="padding:4px 8px;border:1px solid #cbd5e1;border-radius:6px;background:#fff;color:#0f172a;cursor:pointer;opacity:' + (currentPage <= 1 ? '0.5' : '1') + ';">Anterior</button>' +
                '<button type="button" data-devspott-records-next-page="1" ' +
                  (currentPage >= totalPages ? 'disabled ' : '') +
                  'style="padding:4px 8px;border:1px solid #cbd5e1;border-radius:6px;background:#fff;color:#0f172a;cursor:pointer;opacity:' + (currentPage >= totalPages ? '0.5' : '1') + ';">Próxima</button>' +
              '</div>' +
            '</div>';
          host.innerHTML =
            header +
            dashboardSection +
            entityTabs +
            filtersBar +
            quickFiltersBar +
            dateFilterHint +
            emptyReasonsHint +
            table +
            pagination;
          saveRuntimeViewScope();
          if (RUNTIME_RECORDS_VIEW_JUST_RESTORED) {
            RUNTIME_RECORDS_VIEW_JUST_RESTORED = false;
          }
          postRuntimeRecordsPanelToParent(host.innerHTML, true);
        }

        function refreshRuntimeRecordsTableFromCurrentState() {
          var currentState = readContractState();
          if (currentState) {
            renderRuntimeRecordsTable(currentState);
          }
        }

        function triggerDuplicateRecord(row) {
          if (!isPlainObject(row)) {
            return;
          }
          var entityType = normalizeIdentity(row.entity_type);
          if (!entityType) {
            return;
          }
          var clonedData = isPlainObject(row.data) ? toSerializable(row.data) : {};
          if (isPlainObject(clonedData) && !normalizeIdentity(clonedData.idempotency_key)) {
            clonedData.idempotency_key = (window.crypto && crypto.randomUUID)
              ? crypto.randomUUID()
              : 'idem_dup_' + Date.now() + '_' + Math.random().toString(16).slice(2);
          }
          postRuntimeAction('record.create', {
            entity_type: entityType,
            data: isPlainObject(clonedData) ? clonedData : {},
          });
        }

        function triggerDeleteRecord(row) {
          if (!isPlainObject(row)) {
            return;
          }
          var entityType = normalizeIdentity(row.entity_type);
          var recordId = normalizeIdentity(row.record_id);
          var externalId = normalizeIdentity(row.external_id);
          if (!recordId && !externalId) {
            return;
          }
          postRuntimeAction('record.delete', {
            entity_type: entityType || undefined,
            record_id: recordId || undefined,
            external_id: externalId || undefined,
            data: {},
          });
        }

        function handleRuntimeTableInput(target) {
          if (!target) {
            return false;
          }
          var statusFilterSelect = target.closest('[data-devspott-records-status-filter]');
          if (statusFilterSelect) {
            RUNTIME_RECORDS_VIEW.status_filter = String(statusFilterSelect.value || '');
            RUNTIME_RECORDS_VIEW.page = 1;
            emitRuntimeTelemetry('runtime.filters.status.changed', {
              value: RUNTIME_RECORDS_VIEW.status_filter || null,
            });
            saveRuntimeViewScope();
            refreshRuntimeRecordsTableFromCurrentState();
            return true;
          }
          var categoryFilterSelect = target.closest('[data-devspott-records-category-filter]');
          if (categoryFilterSelect) {
            RUNTIME_RECORDS_VIEW.category_filter = String(categoryFilterSelect.value || '');
            RUNTIME_RECORDS_VIEW.page = 1;
            emitRuntimeTelemetry('runtime.filters.category.changed', {
              value: RUNTIME_RECORDS_VIEW.category_filter || null,
            });
            saveRuntimeViewScope();
            refreshRuntimeRecordsTableFromCurrentState();
            return true;
          }
          var dateFromInput = target.closest('[data-devspott-records-date-from]');
          if (dateFromInput) {
            RUNTIME_RECORDS_VIEW.date_from = String(dateFromInput.value || '');
            RUNTIME_RECORDS_VIEW.page = 1;
            emitRuntimeTelemetry('runtime.filters.date_from.changed', {
              value: RUNTIME_RECORDS_VIEW.date_from || null,
            });
            saveRuntimeViewScope();
            refreshRuntimeRecordsTableFromCurrentState();
            return true;
          }
          var dateToInput = target.closest('[data-devspott-records-date-to]');
          if (dateToInput) {
            RUNTIME_RECORDS_VIEW.date_to = String(dateToInput.value || '');
            RUNTIME_RECORDS_VIEW.page = 1;
            emitRuntimeTelemetry('runtime.filters.date_to.changed', {
              value: RUNTIME_RECORDS_VIEW.date_to || null,
            });
            saveRuntimeViewScope();
            refreshRuntimeRecordsTableFromCurrentState();
            return true;
          }
          var searchInput = target.closest('[data-devspott-records-search]');
          if (searchInput) {
            RUNTIME_RECORDS_VIEW.search = String(searchInput.value || '');
            RUNTIME_RECORDS_VIEW.page = 1;
            saveRuntimeViewScope();
            refreshRuntimeRecordsTableFromCurrentState();
            return true;
          }
          var pageSizeSelect = target.closest('[data-devspott-page-size]');
          if (pageSizeSelect) {
            var selectedPageSize = Math.max(
              1,
              parseInt(String(pageSizeSelect.value || RUNTIME_RECORDS_VIEW.page_size || 8), 10) || 8
            );
            RUNTIME_RECORDS_VIEW.page_size = selectedPageSize;
            RUNTIME_RECORDS_VIEW.page = 1;
            emitRuntimeTelemetry('runtime.pagination.page_size.changed', {
              page_size: selectedPageSize,
            });
            saveRuntimeViewScope();
            refreshRuntimeRecordsTableFromCurrentState();
            return true;
          }
          return false;
        }

        function processRuntimeRecordsParentForward(payload) {
          var data = payload || {};
          var interaction = String(data.interaction || '');
          var forward = data.forward || {};
          var attrs = forward.attributes || {};
          var value =
            forward.value === undefined || forward.value === null ? '' : String(forward.value);

          if (interaction === 'escape_search') {
            clearRuntimeSearchFilter();
            return;
          }

          if (interaction === 'change' || interaction === 'input') {
            if (attrs['data-devspott-records-status-filter']) {
              RUNTIME_RECORDS_VIEW.status_filter = value;
              RUNTIME_RECORDS_VIEW.page = 1;
              emitRuntimeTelemetry('runtime.filters.status.changed', {
                value: RUNTIME_RECORDS_VIEW.status_filter || null,
              });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
              return;
            }
            if (attrs['data-devspott-records-category-filter']) {
              RUNTIME_RECORDS_VIEW.category_filter = value;
              RUNTIME_RECORDS_VIEW.page = 1;
              emitRuntimeTelemetry('runtime.filters.category.changed', {
                value: RUNTIME_RECORDS_VIEW.category_filter || null,
              });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
              return;
            }
            if (attrs['data-devspott-records-date-from']) {
              RUNTIME_RECORDS_VIEW.date_from = value;
              RUNTIME_RECORDS_VIEW.page = 1;
              emitRuntimeTelemetry('runtime.filters.date_from.changed', {
                value: RUNTIME_RECORDS_VIEW.date_from || null,
              });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
              return;
            }
            if (attrs['data-devspott-records-date-to']) {
              RUNTIME_RECORDS_VIEW.date_to = value;
              RUNTIME_RECORDS_VIEW.page = 1;
              emitRuntimeTelemetry('runtime.filters.date_to.changed', {
                value: RUNTIME_RECORDS_VIEW.date_to || null,
              });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
              return;
            }
            if (attrs['data-devspott-records-search']) {
              RUNTIME_RECORDS_VIEW.search = value;
              RUNTIME_RECORDS_VIEW.page = 1;
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
              return;
            }
            if (attrs['data-devspott-page-size']) {
              var selectedPageSize = Math.max(
                1,
                parseInt(String(value || RUNTIME_RECORDS_VIEW.page_size || 8), 10) || 8
              );
              RUNTIME_RECORDS_VIEW.page_size = selectedPageSize;
              RUNTIME_RECORDS_VIEW.page = 1;
              emitRuntimeTelemetry('runtime.pagination.page_size.changed', {
                page_size: selectedPageSize,
              });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
              return;
            }
            return;
          }

          if (interaction !== 'click') {
            return;
          }

          if ('data-devspott-entity-chip' in attrs) {
            var entityChipValue = String(attrs['data-devspott-entity-chip'] || '');
            saveRuntimeViewScope();
            applyRuntimeViewScope(entityChipValue);
            RUNTIME_RECORDS_VIEW.page = 1;
            emitRuntimeTelemetry('runtime.filters.entity.changed', {
              value: entityChipValue || null,
            });
            refreshRuntimeRecordsTableFromCurrentState();
            return;
          }

          if ('data-devspott-clear-filters' in attrs) {
            clearRuntimeFilters();
            emitRuntimeTelemetry('runtime.filters.cleared', { source: 'filters_bar' });
            saveRuntimeViewScope();
            refreshRuntimeRecordsTableFromCurrentState();
            return;
          }

          if ('data-devspott-clear-all-filters' in attrs) {
            clearRuntimeFilters();
            emitRuntimeTelemetry('runtime.filters.cleared', { source: 'header' });
            saveRuntimeViewScope();
            refreshRuntimeRecordsTableFromCurrentState();
            return;
          }

          if ('data-devspott-dashboard-status' in attrs) {
            RUNTIME_RECORDS_VIEW.status_filter = String(
              attrs['data-devspott-dashboard-status'] || ''
            );
            RUNTIME_RECORDS_VIEW.page = 1;
            emitRuntimeTelemetry('runtime.dashboard.status.selected', {
              value: RUNTIME_RECORDS_VIEW.status_filter || null,
            });
            saveRuntimeViewScope();
            refreshRuntimeRecordsTableFromCurrentState();
            return;
          }

          if ('data-devspott-dashboard-category' in attrs) {
            RUNTIME_RECORDS_VIEW.category_filter = String(
              attrs['data-devspott-dashboard-category'] || ''
            );
            RUNTIME_RECORDS_VIEW.page = 1;
            emitRuntimeTelemetry('runtime.dashboard.category.selected', {
              value: RUNTIME_RECORDS_VIEW.category_filter || null,
            });
            saveRuntimeViewScope();
            refreshRuntimeRecordsTableFromCurrentState();
            return;
          }

          if ('data-devspott-dashboard-clear-entity' in attrs) {
            saveRuntimeViewScope();
            applyRuntimeViewScope('');
            RUNTIME_RECORDS_VIEW.page = 1;
            emitRuntimeTelemetry('runtime.filters.entity.cleared', { source: 'dashboard' });
            saveRuntimeViewScope();
            refreshRuntimeRecordsTableFromCurrentState();
            return;
          }

          if ('data-devspott-dashboard-clear-status' in attrs) {
            RUNTIME_RECORDS_VIEW.status_filter = '';
            RUNTIME_RECORDS_VIEW.page = 1;
            emitRuntimeTelemetry('runtime.filters.status.cleared', { source: 'dashboard' });
            saveRuntimeViewScope();
            refreshRuntimeRecordsTableFromCurrentState();
            return;
          }

          if ('data-devspott-dashboard-clear-category' in attrs) {
            RUNTIME_RECORDS_VIEW.category_filter = '';
            RUNTIME_RECORDS_VIEW.page = 1;
            emitRuntimeTelemetry('runtime.filters.category.cleared', { source: 'dashboard' });
            saveRuntimeViewScope();
            refreshRuntimeRecordsTableFromCurrentState();
            return;
          }

          if ('data-devspott-empty-action' in attrs) {
            var emptyAction = String(attrs['data-devspott-empty-action'] || '').trim();
            if (emptyAction === 'clear_search') {
              RUNTIME_RECORDS_VIEW.search = '';
              RUNTIME_RECORDS_VIEW.page = 1;
            } else if (emptyAction === 'clear_period') {
              RUNTIME_RECORDS_VIEW.date_from = '';
              RUNTIME_RECORDS_VIEW.date_to = '';
              RUNTIME_RECORDS_VIEW.page = 1;
            } else if (emptyAction === 'clear_all') {
              clearRuntimeFilters();
            }
            emitRuntimeTelemetry('runtime.empty_state.recovery_action', {
              action: emptyAction,
            });
            saveRuntimeViewScope();
            refreshRuntimeRecordsTableFromCurrentState();
            return;
          }

          if ('data-devspott-filter-preset' in attrs) {
            var presetKey = String(attrs['data-devspott-filter-preset'] || '');
            if (applyRuntimeQuickPreset(presetKey)) {
              emitRuntimeTelemetry('runtime.filters.preset.applied', {
                preset: presetKey,
              });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
            }
            return;
          }

          if ('data-devspott-load-record-index' in attrs) {
            var loadIndex = parseInt(attrs['data-devspott-load-record-index'] || '-1', 10);
            if (!Number.isNaN(loadIndex)) {
              openRecordAtIndex(loadIndex);
            }
            return;
          }

          if ('data-devspott-duplicate-record-index' in attrs) {
            var duplicateIndex = parseInt(
              attrs['data-devspott-duplicate-record-index'] || '-1',
              10
            );
            if (
              !Number.isNaN(duplicateIndex) &&
              duplicateIndex >= 0 &&
              duplicateIndex < RUNTIME_RECORDS_CACHE.length
            ) {
              triggerDuplicateRecord(RUNTIME_RECORDS_CACHE[duplicateIndex]);
            }
            return;
          }

          if ('data-devspott-delete-record-index' in attrs) {
            var deleteIndex = parseInt(attrs['data-devspott-delete-record-index'] || '-1', 10);
            if (
              !Number.isNaN(deleteIndex) &&
              deleteIndex >= 0 &&
              deleteIndex < RUNTIME_RECORDS_CACHE.length
            ) {
              confirmDeleteWithUndo(RUNTIME_RECORDS_CACHE[deleteIndex]);
            }
            return;
          }

          if ('data-devspott-sort-key' in attrs) {
            var sortKey = String(attrs['data-devspott-sort-key'] || '').trim();
            if (sortKey) {
              if (String(RUNTIME_RECORDS_VIEW.sort_by || '') === sortKey) {
                RUNTIME_RECORDS_VIEW.sort_dir =
                  String(RUNTIME_RECORDS_VIEW.sort_dir || 'asc') === 'asc' ? 'desc' : 'asc';
              } else {
                RUNTIME_RECORDS_VIEW.sort_by = sortKey;
                RUNTIME_RECORDS_VIEW.sort_dir = 'asc';
              }
              RUNTIME_RECORDS_VIEW.page = 1;
              emitRuntimeTelemetry('runtime.table.sort.changed', {
                sort_by: RUNTIME_RECORDS_VIEW.sort_by,
                sort_dir: RUNTIME_RECORDS_VIEW.sort_dir,
              });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
            }
            return;
          }

          if ('data-devspott-records-prev-page' in attrs) {
            RUNTIME_RECORDS_VIEW.page = Math.max(1, Number(RUNTIME_RECORDS_VIEW.page || 1) - 1);
            emitRuntimeTelemetry('runtime.pagination.changed', {
              direction: 'prev',
              page: Number(RUNTIME_RECORDS_VIEW.page || 1),
            });
            saveRuntimeViewScope();
            refreshRuntimeRecordsTableFromCurrentState();
            return;
          }

          if ('data-devspott-records-next-page' in attrs) {
            RUNTIME_RECORDS_VIEW.page = Number(RUNTIME_RECORDS_VIEW.page || 1) + 1;
            emitRuntimeTelemetry('runtime.pagination.changed', {
              direction: 'next',
              page: Number(RUNTIME_RECORDS_VIEW.page || 1),
            });
            saveRuntimeViewScope();
            refreshRuntimeRecordsTableFromCurrentState();
            return;
          }
        }

        function getRuntimeSearchInput() {
          return document.querySelector('[data-devspott-records-search]');
        }

        function focusRuntimeSearchInput(selectText) {
          try {
            window.parent.postMessage(
              {
                source: CHANNEL_SOURCE,
                type: 'runtime_records_focus_search',
                messageId: MESSAGE_ID,
                select_text: !!selectText,
              },
              '*'
            );
          } catch (e) {}
          return true;
        }

        function clearRuntimeSearchFilter() {
          if (!String(RUNTIME_RECORDS_VIEW.search || '').trim()) {
            return false;
          }
          RUNTIME_RECORDS_VIEW.search = '';
          RUNTIME_RECORDS_VIEW.page = 1;
          emitRuntimeTelemetry('runtime.filters.search.cleared', { source: 'keyboard' });
          saveRuntimeViewScope();
          refreshRuntimeRecordsTableFromCurrentState();
          return true;
        }

        function goToRuntimePage(direction) {
          var nextPage =
            direction > 0
              ? Number(RUNTIME_RECORDS_VIEW.page || 1) + 1
              : Math.max(1, Number(RUNTIME_RECORDS_VIEW.page || 1) - 1);
          if (nextPage === Number(RUNTIME_RECORDS_VIEW.page || 1)) {
            return false;
          }
          if (direction > 0) {
            var nextButton = document.querySelector('[data-devspott-records-next-page]');
            if (nextButton && nextButton.hasAttribute('disabled')) {
              return false;
            }
          } else {
            var prevButton = document.querySelector('[data-devspott-records-prev-page]');
            if (prevButton && prevButton.hasAttribute('disabled')) {
              return false;
            }
          }
          RUNTIME_RECORDS_VIEW.page = nextPage;
          emitRuntimeTelemetry('runtime.pagination.changed', {
            direction: direction > 0 ? 'next' : 'prev',
            page: nextPage,
            source: 'keyboard',
          });
          saveRuntimeViewScope();
          refreshRuntimeRecordsTableFromCurrentState();
          return true;
        }

        function isEditableElement(element) {
          if (!element || typeof element.tagName !== 'string') {
            return false;
          }
          if (element.isContentEditable) {
            return true;
          }
          var tag = String(element.tagName || '').toLowerCase();
          return tag === 'input' || tag === 'textarea' || tag === 'select';
        }

        function handleRuntimeKeyboardShortcuts(event) {
          if (!event || event.defaultPrevented) {
            return;
          }
          var key = String(event.key || '');
          var activeElement = document.activeElement;
          var editableFocused = isEditableElement(activeElement);
          if (key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey && !editableFocused) {
            if (focusRuntimeSearchInput(true)) {
              event.preventDefault();
            }
            return;
          }
          if (event.altKey && !event.metaKey && !event.ctrlKey && !editableFocused) {
            if (key === 'ArrowLeft') {
              if (goToRuntimePage(-1)) {
                event.preventDefault();
              }
              return;
            }
            if (key === 'ArrowRight') {
              if (goToRuntimePage(1)) {
                event.preventDefault();
              }
              return;
            }
          }
          if (key === 'Escape') {
            var searchInput = getRuntimeSearchInput();
            var escapeOnSearch =
              searchInput &&
              activeElement &&
              (activeElement === searchInput || activeElement.closest('[data-devspott-runtime-records]'));
            if (escapeOnSearch || !editableFocused) {
              if (clearRuntimeSearchFilter()) {
                event.preventDefault();
              }
            }
          }
        }

        function extractPersistableRecordData(record) {
          if (!isPlainObject(record)) {
            return null;
          }
          if (isPlainObject(record.data)) {
            var nested = toSerializable(record.data);
            if (isPlainObject(nested)) {
              return nested;
            }
          }
          var cloned = toSerializable(record);
          return isPlainObject(cloned) ? cloned : null;
        }

        function findRecordDataInState(state, entityType, recordId, externalId) {
          var normalizedRecordId = normalizeIdentity(recordId);
          var normalizedExternalId = normalizeIdentity(externalId);
          if (!normalizedRecordId && !normalizedExternalId) {
            return null;
          }
          var records = extractEntityRecordsFromState(state, entityType);
          for (var i = 0; i < records.length; i += 1) {
            var record = records[i];
            if (!isPlainObject(record)) {
              continue;
            }
            var recordData = extractPersistableRecordData(record) || {};
            var candidates = [
              normalizeIdentity(record.id),
              normalizeIdentity(record.record_id),
              normalizeIdentity(record.external_id),
              normalizeIdentity(recordData.id),
              normalizeIdentity(recordData.record_id),
              normalizeIdentity(recordData.external_id),
            ];
            if (
              (normalizedRecordId && candidates.indexOf(normalizedRecordId) >= 0) ||
              (normalizedExternalId && candidates.indexOf(normalizedExternalId) >= 0)
            ) {
              return recordData;
            }
          }
          return null;
        }

        function parseRuntimeDataAttribute(runtimeNode) {
          var rawRuntimeData = runtimeNode.getAttribute('data-runtime-data');
          if (!rawRuntimeData) {
            return null;
          }
          try {
            var parsed = JSON.parse(rawRuntimeData);
            return isPlainObject(parsed) ? parsed : null;
          } catch (error) {
            return null;
          }
        }

        function buildRuntimeData(runtimeAction, runtimeNode) {
          var explicitRuntimeData = parseRuntimeDataAttribute(runtimeNode);
          if (explicitRuntimeData) {
            return explicitRuntimeData;
          }

          var fallbackValues = collectValues();
          var state = readContractState();
          if (!isPlainObject(state)) {
            return fallbackValues;
          }

          var stateFieldValues = isPlainObject(state.field_values)
            ? state.field_values
            : {};
          var normalizedAction = normalizeIdentity(runtimeAction);
          var entityType = readRuntimeEntityTypeAttr(runtimeNode);
          if (!entityType && DOMAIN_SINGLE_ENTITY_TYPE) {
            entityType = normalizeIdentity(DOMAIN_SINGLE_ENTITY_TYPE);
          }
          var recordId = readRuntimeRecordIdAttr(runtimeNode);
          var externalId = normalizeIdentity(runtimeNode.getAttribute('data-external-id'));

          if (normalizedAction === 'record.update' || normalizedAction === 'record.create') {
            var recordData =
              findRecordDataInState(state, entityType, recordId, externalId) || {};
            return Object.assign({}, recordData, stateFieldValues, fallbackValues);
          }

          return Object.assign({}, stateFieldValues, fallbackValues);
        }

        function postAction(actionPayload) {
          window.parent.postMessage(
            Object.assign(
              {
                source: CHANNEL_SOURCE,
                type: 'canvas_action',
                messageId: MESSAGE_ID,
              },
              actionPayload
            ),
            '*'
          );
        }

        function normalizeDownloadPayload(payload) {
          if (!payload || typeof payload !== 'object') {
            return {};
          }

          var normalized = {};
          if (typeof payload.href === 'string') {
            normalized.href = payload.href;
          }
          if (typeof payload.filename === 'string') {
            normalized.filename = payload.filename;
          }
          if (typeof payload.text === 'string') {
            normalized.text = payload.text;
          }
          if (typeof payload.base64 === 'string') {
            normalized.base64 = payload.base64;
          }
          if (typeof payload.mimeType === 'string') {
            normalized.mimeType = payload.mimeType;
          } else if (typeof payload.mime_type === 'string') {
            normalized.mimeType = payload.mime_type;
          }
          return normalized;
        }

        function requestDownload(payload) {
          postAction(
            Object.assign(
              {
                action: 'download',
              },
              normalizeDownloadPayload(payload)
            )
          );
        }

        if (typeof window.__devspottRequestDownload !== 'function') {
          window.__devspottRequestDownload = requestDownload;
        }
        if (typeof window.__devspottDownloadFile !== 'function') {
          window.__devspottDownloadFile = requestDownload;
        }

        if (typeof window.__devspottRuntimeAction !== 'function') {
          window.__devspottRuntimeAction = function (runtimeAction, params) {
            return postRuntimeAction(runtimeAction, params);
          };
        }

        function classifyUrlAction(url, explicitDownload) {
          var href = String(url || '').trim();
          if (!href || href === '#' || /^javascript:/i.test(href)) {
            return null;
          }

          if (/^mailto:/i.test(href)) {
            return { action: 'email', href: href };
          }

          var isDownload =
            !!explicitDownload ||
            /^data:application\\/pdf/i.test(href) ||
            /\\.pdf(\\?.*)?$/i.test(href) ||
            /(^|[\\/?=&_-])pdf([\\/?=&_.-]|$)/i.test(href) ||
            /^blob:/i.test(href);

          if (isDownload) {
            return { action: 'download', href: href };
          }

          return { action: 'open_link', href: href };
        }

        function notifyParentToolFeedback(status, message) {
          try {
            window.parent.postMessage(
              {
                source: CHANNEL_SOURCE,
                type: 'tool_runtime_feedback',
                messageId: MESSAGE_ID,
                status: status,
                message: String(message || ''),
              },
              '*'
            );
          } catch (e) {}
        }

        function bindRuntimeRecordIds(viewModelDelta) {
          var delta = isPlainObject(viewModelDelta) ? viewModelDelta : {};
          var records = Array.isArray(delta.records) ? delta.records : [];
          var entity = normalizeIdentity(delta.default_entity || DOMAIN_SINGLE_ENTITY_TYPE);
          if (!records.length || !entity) {
            return 0;
          }
          var containers = document.querySelectorAll('[data-record-bind="' + entity + '"]');
          if (!containers || !containers.length) {
            return 0;
          }
          var recordsByExternalId = {};
          records.forEach(function (record) {
            if (!isPlainObject(record)) {
              return;
            }
            var ext = normalizeIdentity(record.external_id);
            if (ext) {
              recordsByExternalId[ext] = record;
            }
          });
          var applied = 0;
          containers.forEach(function (container) {
            var rowCandidates = container.querySelectorAll('[data-record-row], li, tr, [data-card]');
            var rows = rowCandidates && rowCandidates.length ? rowCandidates : container.children;
            for (var i = 0; i < rows.length; i += 1) {
              var row = rows[i];
              if (!row || typeof row.getAttribute !== 'function') {
                continue;
              }
              var currentRecordId = normalizeIdentity(row.getAttribute('data-record-id'));
              if (currentRecordId) {
                continue;
              }
              var rowExternalId = normalizeIdentity(row.getAttribute('data-external-id'));
              var sourceRecord = rowExternalId && recordsByExternalId[rowExternalId]
                ? recordsByExternalId[rowExternalId]
                : records[i];
              if (!isPlainObject(sourceRecord)) {
                continue;
              }
              var sourceRecordId = normalizeIdentity(sourceRecord.record_id || sourceRecord.id);
              var sourceExternalId = normalizeIdentity(sourceRecord.external_id);
              if (!sourceRecordId) {
                continue;
              }
              row.setAttribute('data-record-id', sourceRecordId);
              if (!rowExternalId && sourceExternalId) {
                row.setAttribute('data-external-id', sourceExternalId);
              }
              applied += 1;
            }
          });
          if (applied > 0) {
            emitRuntimeTelemetry('runtime.bridge.record_bind.applied', {
              entity: entity,
              count: applied,
            });
          }
          return applied;
        }

        function neutralizeNativeDialogs() {
          window.alert = function (text) {
            notifyParentToolFeedback('info', String(text || ''));
          };
          window.confirm = function (text) {
            notifyParentToolFeedback('warn', String(text || ''));
            return true;
          };
          window.prompt = function (text, defaultValue) {
            notifyParentToolFeedback('info', String(text || ''));
            return defaultValue || '';
          };
        }

        window.addEventListener('load', function () {
          neutralizeNativeDialogs();
          postValues();
          setTimeout(postValues, 250);
        });

        window.addEventListener('message', function (event) {
          var data = event && event.data ? event.data : {};
          if (data.source !== CHANNEL_PARENT_SOURCE || data.messageId !== MESSAGE_ID) {
            return;
          }

          if (data.type === 'request_dom_snapshot') {
            postDomSnapshot();
            return;
          }

          if (data.type === 'request_template_state') {
            postTemplateState();
            return;
          }

          if (data.type === 'runtime_records_forward_interaction') {
            processRuntimeRecordsParentForward(data);
            return;
          }

          if (data.type === 'apply_template_state') {
            var applyResult = setTemplateState(data.state || {});
            window.parent.postMessage(
              {
                source: CHANNEL_SOURCE,
                type: 'template_state_applied',
                messageId: MESSAGE_ID,
                applied: !!applyResult.applied,
                has_contract: !!applyResult.has_contract,
              },
              '*'
            );
            if (applyResult.applied) {
              postValues();
            }
            return;
          }

          if (data.type === 'runtime_action_result') {
            if (window.__devspottStore) {
              var responseRequestId = normalizeIdentity(data.request_id);
              if (responseRequestId) {
                if (typeof window.__devspottStore.get === 'function' && typeof window.__devspottStore.set === 'function') {
                  var activeJobs = window.__devspottStore.get('runtime.activeJobs');
                  if (Array.isArray(activeJobs)) {
                    window.__devspottStore.set('runtime.activeJobs', activeJobs.filter(function(id) { return id !== responseRequestId; }));
                  }
                } else if (window.__devspottStore.runtime && Array.isArray(window.__devspottStore.runtime.activeJobs)) {
                  window.__devspottStore.runtime.activeJobs = window.__devspottStore.runtime.activeJobs.filter(function(id) { return id !== responseRequestId; });
                }
              }

              if (data.status === 'success' && typeof window.__devspottStore.set === 'function') {
                var action = data.runtime_action;
                var res = data.result || {};
                var delta = data.view_model_delta || {};
                
                if (action === 'record.list') {
                  var entityType = delta.default_entity || DOMAIN_SINGLE_ENTITY_TYPE;
                  if (entityType) {
                    var items = Array.isArray(res) ? res : (Array.isArray(res.items) ? res.items : []);
                    window.__devspottStore.set('records.' + entityType, items);
                  }
                } else if (action === 'record.get' || action === 'record.create' || action === 'record.update') {
                  var record = res.record || res;
                  var entityType = (delta.entity_record_delta && delta.entity_record_delta.entity_type) || record.entity_type || DOMAIN_SINGLE_ENTITY_TYPE;
                  if (entityType && isPlainObject(record)) {
                    var recordId = record.record_id || record.id;
                    if (recordId) {
                      if (action === 'record.create' && typeof window.__devspottStore.push === 'function') {
                        window.__devspottStore.push('records.' + entityType, record);
                      } else if (typeof window.__devspottStore.replaceById === 'function') {
                        window.__devspottStore.replaceById('records.' + entityType, recordId, record);
                      }
                    }
                  }
                } else if (action === 'record.delete') {
                  var entityType = (delta.entity_record_delta && delta.entity_record_delta.entity_type) || DOMAIN_SINGLE_ENTITY_TYPE;
                  var recordId = delta.entity_record_delta && delta.entity_record_delta.record_id;
                  if (entityType && recordId && typeof window.__devspottStore.removeById === 'function') {
                    window.__devspottStore.removeById('records.' + entityType, recordId);
                  }
                }
              }
            }
            if (isPlainObject(data.view_model_delta)) {
              bindRuntimeRecordIds(data.view_model_delta);
            }
            if (
              data.view_model_delta &&
              typeof window.__devspottApplyRuntimeViewModelDelta === 'function'
            ) {
              try {
                window.__devspottApplyRuntimeViewModelDelta(data.view_model_delta);
              } catch (error) {}
            }
            var currentState = readContractState();
            if (currentState) {
              renderRuntimeRecordsTable(currentState);
            }
            if (data.runtime_action !== 'runtime.telemetry' && !data.silent) {
              var isErr = data.status === 'error';
              notifyParentToolFeedback(
                isErr ? 'error' : 'success',
                String(data.message || (isErr ? 'Erro' : 'Sucesso'))
              );
            }
            var responseRequestId = normalizeIdentity(data.request_id);
            if (responseRequestId && data.status === 'success') {
              delete RUNTIME_CREATE_REQUEST_ID_TO_KEY[responseRequestId];
            }
            return;
          }

          if (data.type !== 'apply_fields') {
            return;
          }

          applyValues(data.values || {});
          postValues();
        });

        document.addEventListener(
          'click',
          function (event) {
            var target = event.target;
            if (!target || typeof target.closest !== 'function') {
              return;
            }

            var runtimeNode = target.closest('[data-runtime-action]');
            if (runtimeNode) {
              var runtimeAction = String(runtimeNode.getAttribute('data-runtime-action') || '').trim();
              if (runtimeAction) {
                var runtimeData = buildRuntimeData(runtimeAction, runtimeNode);
                event.preventDefault();
                var etResolved = readRuntimeEntityTypeAttr(runtimeNode) || DOMAIN_SINGLE_ENTITY_TYPE || undefined;
                var recordIdResolved = readRuntimeRecordIdAttr(runtimeNode) || undefined;
                if (
                  runtimeAction === 'record.create' &&
                  isPlainObject(runtimeData) &&
                  !normalizeIdentity(runtimeData.idempotency_key)
                ) {
                  runtimeData.idempotency_key = resolveOrCreateIdempotencyKey(runtimeNode, runtimeData);
                  emitRuntimeTelemetry('runtime.bridge.idempotency.injected', {
                    source: 'iframe_click',
                    runtime_action: runtimeAction,
                  });
                }
                var requestId = postRuntimeAction(runtimeAction, {
                  entity_type: etResolved,
                  record_id: recordIdResolved,
                  external_id: runtimeNode.getAttribute('data-external-id') || undefined,
                  data: runtimeData,
                });
                if (
                  runtimeAction === 'record.create' &&
                  isPlainObject(runtimeData) &&
                  normalizeIdentity(runtimeData.idempotency_key)
                ) {
                  RUNTIME_CREATE_REQUEST_ID_TO_KEY[requestId] = normalizeIdentity(
                    runtimeData.idempotency_key
                  );
                }
                return;
              }
            }

            var entityChip = target.closest('[data-devspott-entity-chip]');
            if (entityChip) {
              var entityChipValue = String(entityChip.getAttribute('data-devspott-entity-chip') || '');
              saveRuntimeViewScope();
              applyRuntimeViewScope(entityChipValue);
              RUNTIME_RECORDS_VIEW.page = 1;
              emitRuntimeTelemetry('runtime.filters.entity.changed', {
                value: entityChipValue || null,
              });
              refreshRuntimeRecordsTableFromCurrentState();
              event.preventDefault();
              return;
            }

            var clearFiltersButton = target.closest('[data-devspott-clear-filters]');
            if (clearFiltersButton) {
              clearRuntimeFilters();
              emitRuntimeTelemetry('runtime.filters.cleared', { source: 'filters_bar' });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
              event.preventDefault();
              return;
            }

            var clearAllFiltersButton = target.closest('[data-devspott-clear-all-filters]');
            if (clearAllFiltersButton) {
              clearRuntimeFilters();
              emitRuntimeTelemetry('runtime.filters.cleared', { source: 'header' });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
              event.preventDefault();
              return;
            }

            var dashboardStatusButton = target.closest('[data-devspott-dashboard-status]');
            if (dashboardStatusButton) {
              RUNTIME_RECORDS_VIEW.status_filter = String(
                dashboardStatusButton.getAttribute('data-devspott-dashboard-status') || ''
              );
              RUNTIME_RECORDS_VIEW.page = 1;
              emitRuntimeTelemetry('runtime.dashboard.status.selected', {
                value: RUNTIME_RECORDS_VIEW.status_filter || null,
              });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
              event.preventDefault();
              return;
            }

            var dashboardCategoryButton = target.closest('[data-devspott-dashboard-category]');
            if (dashboardCategoryButton) {
              RUNTIME_RECORDS_VIEW.category_filter = String(
                dashboardCategoryButton.getAttribute('data-devspott-dashboard-category') || ''
              );
              RUNTIME_RECORDS_VIEW.page = 1;
              emitRuntimeTelemetry('runtime.dashboard.category.selected', {
                value: RUNTIME_RECORDS_VIEW.category_filter || null,
              });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
              event.preventDefault();
              return;
            }

            var clearEntityButton = target.closest('[data-devspott-dashboard-clear-entity]');
            if (clearEntityButton) {
              saveRuntimeViewScope();
              applyRuntimeViewScope('');
              RUNTIME_RECORDS_VIEW.page = 1;
              emitRuntimeTelemetry('runtime.filters.entity.cleared', { source: 'dashboard' });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
              event.preventDefault();
              return;
            }

            var clearStatusButton = target.closest('[data-devspott-dashboard-clear-status]');
            if (clearStatusButton) {
              RUNTIME_RECORDS_VIEW.status_filter = '';
              RUNTIME_RECORDS_VIEW.page = 1;
              emitRuntimeTelemetry('runtime.filters.status.cleared', { source: 'dashboard' });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
              event.preventDefault();
              return;
            }

            var clearCategoryButton = target.closest('[data-devspott-dashboard-clear-category]');
            if (clearCategoryButton) {
              RUNTIME_RECORDS_VIEW.category_filter = '';
              RUNTIME_RECORDS_VIEW.page = 1;
              emitRuntimeTelemetry('runtime.filters.category.cleared', { source: 'dashboard' });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
              event.preventDefault();
              return;
            }

            var emptyActionButton = target.closest('[data-devspott-empty-action]');
            if (emptyActionButton) {
              var emptyAction = String(
                emptyActionButton.getAttribute('data-devspott-empty-action') || ''
              ).trim();
              if (emptyAction === 'clear_search') {
                RUNTIME_RECORDS_VIEW.search = '';
                RUNTIME_RECORDS_VIEW.page = 1;
              } else if (emptyAction === 'clear_period') {
                RUNTIME_RECORDS_VIEW.date_from = '';
                RUNTIME_RECORDS_VIEW.date_to = '';
                RUNTIME_RECORDS_VIEW.page = 1;
              } else if (emptyAction === 'clear_all') {
                clearRuntimeFilters();
              }
              emitRuntimeTelemetry('runtime.empty_state.recovery_action', {
                action: emptyAction,
              });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
              event.preventDefault();
              return;
            }

            var presetFilterButton = target.closest('[data-devspott-filter-preset]');
            if (presetFilterButton) {
              var presetKey = String(
                presetFilterButton.getAttribute('data-devspott-filter-preset') || ''
              );
              if (applyRuntimeQuickPreset(presetKey)) {
                emitRuntimeTelemetry('runtime.filters.preset.applied', {
                  preset: presetKey,
                });
                saveRuntimeViewScope();
                refreshRuntimeRecordsTableFromCurrentState();
              }
              event.preventDefault();
              return;
            }

            var loadRecordButton = target.closest('[data-devspott-load-record-index]');
            if (loadRecordButton) {
              var index = parseInt(loadRecordButton.getAttribute('data-devspott-load-record-index') || '-1', 10);
              if (!Number.isNaN(index)) {
                openRecordAtIndex(index);
              }
              event.preventDefault();
              return;
            }

            var duplicateRecordButton = target.closest('[data-devspott-duplicate-record-index]');
            if (duplicateRecordButton) {
              var duplicateIndex = parseInt(
                duplicateRecordButton.getAttribute('data-devspott-duplicate-record-index') || '-1',
                10
              );
              if (
                !Number.isNaN(duplicateIndex) &&
                duplicateIndex >= 0 &&
                duplicateIndex < RUNTIME_RECORDS_CACHE.length
              ) {
                triggerDuplicateRecord(RUNTIME_RECORDS_CACHE[duplicateIndex]);
              }
              event.preventDefault();
              return;
            }

            var deleteRecordButton = target.closest('[data-devspott-delete-record-index]');
            if (deleteRecordButton) {
              var deleteIndex = parseInt(
                deleteRecordButton.getAttribute('data-devspott-delete-record-index') || '-1',
                10
              );
              if (
                !Number.isNaN(deleteIndex) &&
                deleteIndex >= 0 &&
                deleteIndex < RUNTIME_RECORDS_CACHE.length
              ) {
                confirmDeleteWithUndo(RUNTIME_RECORDS_CACHE[deleteIndex]);
              }
              event.preventDefault();
              return;
            }

            var sortButton = target.closest('[data-devspott-sort-key]');
            if (sortButton) {
              var sortKey = String(sortButton.getAttribute('data-devspott-sort-key') || '').trim();
              if (sortKey) {
                if (String(RUNTIME_RECORDS_VIEW.sort_by || '') === sortKey) {
                  RUNTIME_RECORDS_VIEW.sort_dir =
                    String(RUNTIME_RECORDS_VIEW.sort_dir || 'asc') === 'asc' ? 'desc' : 'asc';
                } else {
                  RUNTIME_RECORDS_VIEW.sort_by = sortKey;
                  RUNTIME_RECORDS_VIEW.sort_dir = 'asc';
                }
                RUNTIME_RECORDS_VIEW.page = 1;
                emitRuntimeTelemetry('runtime.table.sort.changed', {
                  sort_by: RUNTIME_RECORDS_VIEW.sort_by,
                  sort_dir: RUNTIME_RECORDS_VIEW.sort_dir,
                });
                saveRuntimeViewScope();
                refreshRuntimeRecordsTableFromCurrentState();
              }
              event.preventDefault();
              return;
            }

            if (target.closest('[data-devspott-records-prev-page]')) {
              RUNTIME_RECORDS_VIEW.page = Math.max(1, Number(RUNTIME_RECORDS_VIEW.page || 1) - 1);
              emitRuntimeTelemetry('runtime.pagination.changed', {
                direction: 'prev',
                page: Number(RUNTIME_RECORDS_VIEW.page || 1),
              });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
              event.preventDefault();
              return;
            }

            if (target.closest('[data-devspott-records-next-page]')) {
              RUNTIME_RECORDS_VIEW.page = Number(RUNTIME_RECORDS_VIEW.page || 1) + 1;
              emitRuntimeTelemetry('runtime.pagination.changed', {
                direction: 'next',
                page: Number(RUNTIME_RECORDS_VIEW.page || 1),
              });
              saveRuntimeViewScope();
              refreshRuntimeRecordsTableFromCurrentState();
              event.preventDefault();
              return;
            }

            var anchor = target.closest('a');
            if (!anchor) {
              return;
            }
            var href = anchor.getAttribute('href') || '';
            var downloadName = anchor.getAttribute('download') || undefined;
            var actionPayload = classifyUrlAction(
              href,
              anchor.hasAttribute('download')
            );
            if (!actionPayload) {
              return;
            }
            event.preventDefault();
            postAction(
              Object.assign({}, actionPayload, {
                filename: downloadName,
              })
            );
          },
          true
        );
        window.print = function () {
          postAction({ action: 'print' });
          return undefined;
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText = function (text) {
            postAction({
              action: 'copy',
              text: text == null ? '' : String(text),
            });
            return Promise.resolve();
          };
        }
        document.addEventListener(
          'copy',
          function () {
            var selectedText = '';
            try {
              selectedText = String(window.getSelection ? window.getSelection() : '');
            } catch (e) {}
            if (selectedText) {
              postAction({ action: 'copy', text: selectedText });
            }
          },
          true
        );
        var originalWindowOpen = window.open ? window.open.bind(window) : null;
        window.open = function (url, target, features) {
          var actionPayload = classifyUrlAction(url, false);
          if (actionPayload) {
            postAction(actionPayload);
            return null;
          }
          if (originalWindowOpen) {
            return originalWindowOpen(url, target, features);
          }
          return null;
        };

        function normalizeNumericString(raw) {
          if (raw === '' || raw == null) return '';
          var cleaned = String(raw).replace(/[^0-9.,\-eE]/g, '').trim();
          if (!cleaned) return '';
          var hasComma = cleaned.indexOf(',') !== -1;
          var hasDot = cleaned.indexOf('.') !== -1;
          if (hasComma && hasDot) {
            cleaned = cleaned.replace(/\./g, '').replace(',', '.');
          } else if (hasComma) {
            cleaned = cleaned.replace(',', '.');
          }
          return cleaned;
        }

        function coerceFieldValueByType(rawValue, declaredType) {
          var type = String(declaredType || '').trim().toLowerCase();
          if (!type) return rawValue;
          if (type === 'number' || type === 'float' || type === 'decimal' || type === 'money' || type === 'currency') {
            if (rawValue === '' || rawValue == null) return null;
            var normalized = normalizeNumericString(rawValue);
            if (!normalized) return null;
            var parsed = parseFloat(normalized);
            return isNaN(parsed) ? null : parsed;
          }
          if (type === 'integer' || type === 'int') {
            if (rawValue === '' || rawValue == null) return null;
            var normalizedInt = normalizeNumericString(rawValue);
            if (!normalizedInt) return null;
            var parsedInt = parseFloat(normalizedInt);
            return isNaN(parsedInt) ? null : Math.trunc(parsedInt);
          }
          if (type === 'boolean' || type === 'bool') {
            if (typeof rawValue === 'boolean') return rawValue;
            if (rawValue === '' || rawValue == null) return null;
            var lowered = String(rawValue).trim().toLowerCase();
            if (lowered === 'true' || lowered === '1' || lowered === 'yes' || lowered === 'sim' || lowered === 'on') return true;
            if (lowered === 'false' || lowered === '0' || lowered === 'no' || lowered === 'nao' || lowered === 'não' || lowered === 'off') return false;
            return null;
          }
          if (type === 'array') {
            if (Array.isArray(rawValue)) return rawValue;
            if (rawValue === '' || rawValue == null) return [];
            return String(rawValue)
              .split(',')
              .map(function (token) { return token.trim(); })
              .filter(function (token) { return token.length > 0; });
          }
          return rawValue;
        }

        function buildPayloadFromForm(formEl) {
          var payload = {};
          if (!formEl || typeof formEl.querySelectorAll !== 'function') {
            return payload;
          }
          var fields = formEl.querySelectorAll('[data-field]');
          for (var i = 0; i < fields.length; i++) {
            var el = fields[i];
            var name = el.getAttribute('data-field');
            if (!name) continue;
            var declaredType = (el.getAttribute && el.getAttribute('data-type')) || '';
            var value = null;
            if (el.tagName === 'INPUT' && (el.type === 'checkbox' || el.type === 'radio')) {
              value = el.checked ? (el.value || true) : null;
            } else if (declaredType) {
              value = coerceFieldValueByType(el.value, declaredType);
            } else if (el.tagName === 'INPUT' && el.type === 'number') {
              value = el.value === '' || el.value == null ? null : parseFloat(el.value);
              if (typeof value === 'number' && isNaN(value)) value = null;
            } else if (el.tagName === 'SELECT' && el.multiple) {
              var picked = [];
              for (var j = 0; j < el.options.length; j++) {
                if (el.options[j].selected) picked.push(el.options[j].value);
              }
              value = picked;
            } else {
              value = el.value === '' || el.value == null ? null : el.value;
            }
            payload[name] = value;
          }
          return payload;
        }

        function resolveFormRecordId(formEl) {
          if (!formEl || typeof formEl.querySelector !== 'function') {
            return null;
          }
          var hidden = formEl.querySelector('input[type="hidden"][data-record-id-input]')
            || formEl.querySelector('input[type="hidden"]#recordId')
            || formEl.querySelector('input[type="hidden"][name="recordId"]')
            || formEl.querySelector('input[type="hidden"][name="record_id"]');
          if (hidden && hidden.value) return hidden.value;
          var explicit = formEl.getAttribute && formEl.getAttribute('data-record-id');
          return explicit || null;
        }

        function resolveFormRuntimeAction(formEl) {
          if (!formEl) return null;
          var attr = formEl.getAttribute && formEl.getAttribute('data-runtime-action');
          if (attr) return String(attr).trim().toLowerCase();
          var submitBtn = formEl.querySelector
            ? formEl.querySelector('[type="submit"][data-runtime-action], button[type="submit"][data-runtime-action]')
            : null;
          if (submitBtn) {
            var btnAttr = submitBtn.getAttribute('data-runtime-action');
            if (btnAttr) return String(btnAttr).trim().toLowerCase();
          }
          return null;
        }

        function resolveFormEntityType(formEl) {
          if (!formEl || !formEl.getAttribute) return null;
          var entity =
            formEl.getAttribute('data-entity-type') ||
            formEl.getAttribute('data-entity-name') ||
            formEl.getAttribute('data-devspott-repair-entity');
          if (entity) return String(entity).trim();
          var hint = formEl.querySelector ? formEl.querySelector('[data-entity-type]') : null;
          if (hint) {
            var hintAttr = hint.getAttribute('data-entity-type');
            if (hintAttr) return String(hintAttr).trim();
          }
          return null;
        }

        document.addEventListener('submit', function (event) {
          event.preventDefault();
          var formEl = event && event.target && event.target.tagName === 'FORM'
            ? event.target
            : null;
          if (!formEl) return;
          var runtimeAction = resolveFormRuntimeAction(formEl);
          var hasRepairMarker = formEl.getAttribute &&
            formEl.getAttribute('data-devspott-repair-form-wire') === '1';
          if (!runtimeAction && !hasRepairMarker) return;
          var entityType = resolveFormEntityType(formEl);
          if (!entityType) return;
          try {
            if (formEl.checkValidity && !formEl.checkValidity()) {
              if (formEl.reportValidity) formEl.reportValidity();
              event.stopImmediatePropagation();
              return;
            }
            var payload = buildPayloadFromForm(formEl);
            var recordId = resolveFormRecordId(formEl);
            var action = runtimeAction;
            if (!action || action === 'record.auto') {
              action = recordId ? 'record.update' : 'record.create';
            }
            var args = { entity_type: entityType, data: payload };
            if (recordId) args.record_id = recordId;
            if (action === 'record.create') {
              args.idempotency_key =
                (window.crypto && crypto.randomUUID)
                  ? crypto.randomUUID()
                  : ('frm_' + Date.now() + '_' + Math.random().toString(16).slice(2));
            }
            postRuntimeAction(action, args);
            event.stopImmediatePropagation();
          } catch (err) {}
        }, true);
        document.addEventListener(
          'input',
          function (event) {
            if (handleRuntimeTableInput(event && event.target)) {
              return;
            }
            postValues();
          },
          true
        );
        document.addEventListener(
          'change',
          function (event) {
            if (handleRuntimeTableInput(event && event.target)) {
              return;
            }
            postValues();
          },
          true
        );
        document.addEventListener(
          'keydown',
          function (event) {
            handleRuntimeKeyboardShortcuts(event);
          },
          true
        );
      })();
    </script>
  `;

  if (/<\/body>/i.test(html)) {
    return html.replace(/<\/body>/i, `${bridgeScript}</body>`);
  }

  return `${html}${bridgeScript}`;
};

const ToolViewer: React.FC = () => {
  const { t } = useTranslation();
  const { setPageInfo, setHeaderActions } = usePage();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const { toolId } = useParams<{ toolId: string }>();

  const [isLoading, setIsLoading] = useState(false);
  const [tool, setTool] = useState<ToolTemplate | null>(null);
  const toolSubtitle = useMemo(() => {
    const schema = tool?.schema;
    if (schema && typeof schema === 'object') {
      const presentationSpec = (schema as { presentation_spec?: unknown }).presentation_spec;
      if (presentationSpec && typeof presentationSpec === 'object') {
        const subtitle = String(
          (presentationSpec as { subtitle?: unknown }).subtitle || ''
        ).trim();
        if (subtitle) {
          return subtitle;
        }
      }
    }
    return t('tools.viewerSubtitle');
  }, [t, tool?.schema]);
  const singleEntityTypeFromTool = useMemo(
    () => extractRuntimeEntityNamesFromTool(tool),
    [tool]
  );
  const singleEntityTypeFallback =
    singleEntityTypeFromTool.length === 1 ? singleEntityTypeFromTool[0] : undefined;
  const [isDeveloperDrawerOpen, setIsDeveloperDrawerOpen] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState('embed');
  const fullscreenRootRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const templateStateResolverRef = useRef<((result: TemplateStateResult) => void) | null>(
    null
  );
  const templateStateApplyResolverRef = useRef<
    ((result: TemplateStateApplyResult) => void) | null
  >(null);
  const domSnapshotResolverRef = useRef<((html: string | null) => void) | null>(null);
  const rehydrationRunIdRef = useRef(0);
  const contractErrorShownRef = useRef(false);
  const runtimeStateTokenRef = useRef<string | null>(null);
  const statePersistRecordIdRef = useRef<string | null>(null);
  const statePersistTimerRef = useRef<number | null>(null);
  const statePersistInFlightRef = useRef(false);

  useEffect(() => {
    setPageInfo(tool?.name || t('tools.viewerTitle'), toolSubtitle);
  }, [setPageInfo, t, tool?.name, toolSubtitle]);

  useEffect(() => {
    if (!toolId) {
      return;
    }

    const loadTool = async () => {
      setIsLoading(true);
      try {
        const response = await toolService.getById(toolId);
        setTool(response);
      } finally {
        setIsLoading(false);
      }
    };

    void loadTool();
  }, [toolId]);

  useEffect(() => {
    runtimeStateTokenRef.current = null;
    statePersistRecordIdRef.current = null;
    statePersistInFlightRef.current = false;
    if (statePersistTimerRef.current) {
      window.clearTimeout(statePersistTimerRef.current);
      statePersistTimerRef.current = null;
    }
  }, [tool?.id]);

  useEffect(
    () => () => {
      if (statePersistTimerRef.current) {
        window.clearTimeout(statePersistTimerRef.current);
        statePersistTimerRef.current = null;
      }
    },
    []
  );

  const iframeDoc = useMemo(
    () =>
      buildCanvasDocument(
        tool?.html_content || '',
        `tool-${tool?.id || 'viewer'}`
      ),
    [tool?.html_content, tool?.id]
  );
  const spawnedFrontendUrl = useMemo(() => {
    const runtimeConfig =
      tool?.runtime_config && typeof tool.runtime_config === 'object'
        ? (tool.runtime_config as Record<string, unknown>)
        : null;
    const lastSpawn =
      runtimeConfig?.last_spawn && typeof runtimeConfig.last_spawn === 'object'
        ? (runtimeConfig.last_spawn as Record<string, unknown>)
        : null;
    return String(lastSpawn?.frontend_url || '').trim();
  }, [tool?.runtime_config]);
  const isSpawnedIframeMode = Boolean(spawnedFrontendUrl);

  const isToolOwner = useMemo(() => {
    if (!tool || !currentOrganization) {
      return false;
    }
    return tool.organization_id === currentOrganization.id;
  }, [tool, currentOrganization]);

  /** Ferramenta em camada fixa ocupando toda a viewport (não é Fullscreen API do browser). */
  const [isToolViewportFill, setIsToolViewportFill] = useState(false);

  useEffect(() => {
    setIsToolViewportFill(false);
  }, [tool?.id]);

  useEffect(() => {
    if (!isToolViewportFill) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isToolViewportFill]);

  useEffect(() => {
    if (!isToolViewportFill) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsToolViewportFill(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isToolViewportFill]);

  useEffect(() => {
    const fullscreenButton = tool ? (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setIsToolViewportFill(v => !v)}
        aria-pressed={isToolViewportFill}
        title={isToolViewportFill ? t('tools.exitFullscreen') : t('tools.fullscreen')}
      >
        {isToolViewportFill ? (
          <Minimize2 className="h-4 w-4 sm:mr-1.5" aria-hidden />
        ) : (
          <Maximize2 className="h-4 w-4 sm:mr-1.5" aria-hidden />
        )}
        <span className="hidden sm:inline">
          {isToolViewportFill ? t('tools.exitFullscreen') : t('tools.fullscreen')}
        </span>
      </Button>
    ) : null;

    const settingsButton =
      tool && isToolOwner ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setIsDeveloperDrawerOpen(true)}
        >
          {t('tools.appSettingsTitle')}
        </Button>
      ) : null;

    const rightActions =
      fullscreenButton || settingsButton ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {fullscreenButton}
          {settingsButton}
        </div>
      ) : null;

    setHeaderActions(null, rightActions);
    return () => {
      setHeaderActions(null, null);
    };
  }, [isToolViewportFill, isToolOwner, setHeaderActions, t, tool]);

  const settingsTabs = useMemo(() => {
    if (!tool) {
      return [];
    }
    return [
      {
        id: 'embed',
        label: t('tools.embedPanel.title'),
        content: <EmbedKeyPanel toolId={tool.id} />,
      },
      {
        id: 'api',
        label: t('tools.apiDocs.title'),
        content: <ApiDocsPanel toolId={tool.id} />,
      },
    ];
  }, [tool, t]);

  const showMissingContractErrorOnce = useCallback(() => {
    if (isSpawnedIframeMode) {
      return;
    }
    if (contractErrorShownRef.current) {
      return;
    }
    contractErrorShownRef.current = true;
    toolToast.error(
      'Template precisa implementar window.__devspottGetState e window.__devspottSetState para salvar estado dinâmico.'
    );
  }, [isSpawnedIframeMode]);

  const requestTemplateState = useCallback(async () => {
    if (!tool?.id || !iframeRef.current?.contentWindow) {
      return {
        hasContract: false,
        responded: false,
        state: null as Record<string, unknown> | null,
      };
    }

    return new Promise<TemplateStateResult>(resolve => {
      let settled = false;
      const timeoutId = window.setTimeout(() => {
        if (settled) {
          return;
        }
        settled = true;
        templateStateResolverRef.current = null;
        resolve({ hasContract: false, responded: false, state: null });
      }, 900);

      templateStateResolverRef.current = result => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timeoutId);
        resolve(result);
      };

      iframeRef.current?.contentWindow?.postMessage(
        {
          source: 'devspott-canvas-parent',
          type: 'request_template_state',
          messageId: `tool-${tool.id}`,
        },
        '*'
      );
    });
  }, [tool?.id]);

  const requestDomSnapshot = useCallback(async () => {
    if (!tool?.id || !iframeRef.current?.contentWindow) {
      return null as string | null;
    }
    return new Promise<string | null>(resolve => {
      let settled = false;
      const timeoutId = window.setTimeout(() => {
        if (settled) {
          return;
        }
        settled = true;
        domSnapshotResolverRef.current = null;
        resolve(null);
      }, 900);

      domSnapshotResolverRef.current = html => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timeoutId);
        resolve(typeof html === 'string' && html.trim() ? html : null);
      };

      iframeRef.current?.contentWindow?.postMessage(
        {
          source: 'devspott-canvas-parent',
          type: 'request_dom_snapshot',
          messageId: `tool-${tool.id}`,
        },
        '*'
      );
    });
  }, [tool?.id]);

  const applyTemplateStateToIframe = useCallback(
    async (state: Record<string, unknown> | null, targetToolId?: string) => {
      const resolvedToolId = targetToolId || tool?.id;
      if (!resolvedToolId || !iframeRef.current?.contentWindow || !state) {
        return { applied: false, hasContract: false, responded: false };
      }

      return new Promise<TemplateStateApplyResult>(resolve => {
        let settled = false;
        const timeoutId = window.setTimeout(() => {
          if (settled) {
            return;
          }
          settled = true;
          templateStateApplyResolverRef.current = null;
          resolve({ applied: false, hasContract: false, responded: false });
        }, 1000);

        templateStateApplyResolverRef.current = result => {
          if (settled) {
            return;
          }
          settled = true;
          window.clearTimeout(timeoutId);
          resolve(result);
        };

        iframeRef.current?.contentWindow?.postMessage(
          {
            source: 'devspott-canvas-parent',
            type: 'apply_template_state',
            messageId: `tool-${resolvedToolId}`,
            state,
          },
          '*'
        );
      });
    },
    [tool?.id]
  );

  const resolveScreenStateExternalId = useCallback(
    (currentToolId: string): string => {
      const normalizedToolId = String(currentToolId || '').trim();
      const userId =
        user && typeof user.id === 'number' && Number.isFinite(user.id) ? String(user.id) : '';
      if (userId) {
        return `screen_state:${normalizedToolId}:user:${userId}`;
      }
      return `screen_state:${normalizedToolId}:global`;
    },
    [user]
  );

  const resolveLatestScreenStateRecord = useCallback(
    (
      records: Record<string, unknown>[],
      currentToolId: string
    ): { recordId: string | null; state: Record<string, unknown> | null } => {
      if (!Array.isArray(records) || records.length === 0) {
        return { recordId: null, state: null };
      }
      const preferredExternalId = resolveScreenStateExternalId(currentToolId);
      const preferred = records.find(
        item => String((item as { external_id?: unknown }).external_id || '').trim() === preferredExternalId
      );
      const selected = preferred || records[0];
      if (!selected || typeof selected !== 'object') {
        return { recordId: null, state: null };
      }
      const record = selected as Record<string, unknown>;
      const recordId = String(record.record_id || record.id || '').trim() || null;
      const data = record.data && typeof record.data === 'object' ? (record.data as Record<string, unknown>) : {};
      const storedState =
        data.state && typeof data.state === 'object' ? ({ ...(data.state as Record<string, unknown>) } as Record<string, unknown>) : null;
      if (!storedState) {
        return { recordId, state: null };
      }
      const domSnapshot = String(data.dom_snapshot || '').trim();
      if (domSnapshot) {
        storedState.dom_snapshot = domSnapshot;
      }
      return { recordId, state: storedState };
    },
    [resolveScreenStateExternalId]
  );

  const persistTemplateStateSnapshot = useCallback(
    async (
      messageId: string,
      reason: string,
      templateStateOverride?: Record<string, unknown> | null
    ) => {
      if (isSpawnedIframeMode) {
        return;
      }
      if (!tool?.id || tool.public || !isToolOwner || statePersistInFlightRef.current) {
        return;
      }
      statePersistInFlightRef.current = true;
      try {
        const templateStateResult = templateStateOverride
          ? { hasContract: true, responded: true, state: templateStateOverride }
          : await requestTemplateState();
        if (!templateStateResult.hasContract || !templateStateResult.state) {
          return;
        }
        const domSnapshot = await requestDomSnapshot();
        const externalId = resolveScreenStateExternalId(tool.id);
        const snapshotData: Record<string, unknown> = {
          state: templateStateResult.state,
          persisted_at: new Date().toISOString(),
          reason,
        };
        if (domSnapshot) {
          snapshotData.dom_snapshot = domSnapshot;
        }

        const sendRuntimeAction = async (
          actionId: 'record.create' | 'record.update',
          payload: Record<string, unknown>
        ) =>
          toolRuntimeService.executeRuntimeAction(tool.id, {
            action_id: actionId,
            payload,
            state_token: runtimeStateTokenRef.current || undefined,
          });

        let runtimeResponse;
        if (statePersistRecordIdRef.current) {
          runtimeResponse = await sendRuntimeAction('record.update', {
            entity_type: SCREEN_STATE_ENTITY_TYPE,
            record_id: statePersistRecordIdRef.current,
            external_id: externalId,
            data: snapshotData,
          });
        } else {
          runtimeResponse = await sendRuntimeAction('record.create', {
            entity_type: SCREEN_STATE_ENTITY_TYPE,
            external_id: externalId,
            data: snapshotData,
            is_active: true,
          });
        }

        runtimeStateTokenRef.current = String(runtimeResponse.state_token || '').trim() || null;
        const resultRecord =
          runtimeResponse.result && typeof runtimeResponse.result.record === 'object'
            ? (runtimeResponse.result.record as Record<string, unknown>)
            : null;
        const resolvedRecordId = String(resultRecord?.id || resultRecord?.record_id || '').trim();
        if (resolvedRecordId) {
          statePersistRecordIdRef.current = resolvedRecordId;
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Erro ao persistir estado dinâmico.';
        // Fallback comum: create inicial pode conflitar por external_id já existente.
        if (
          !statePersistRecordIdRef.current &&
          message.toLowerCase().includes('external_id') &&
          tool?.id
        ) {
          try {
            const externalId = resolveScreenStateExternalId(tool.id);
            const templateStateResult = templateStateOverride
              ? { hasContract: true, responded: true, state: templateStateOverride }
              : await requestTemplateState();
            if (templateStateResult.hasContract && templateStateResult.state) {
              const retry = await toolRuntimeService.executeRuntimeAction(tool.id, {
                action_id: 'record.update',
                payload: {
                  entity_type: SCREEN_STATE_ENTITY_TYPE,
                  external_id: externalId,
                  data: {
                    state: templateStateResult.state,
                    persisted_at: new Date().toISOString(),
                    reason,
                  },
                },
                state_token: runtimeStateTokenRef.current || undefined,
              });
              runtimeStateTokenRef.current = String(retry.state_token || '').trim() || null;
              const retryRecord =
                retry.result && typeof retry.result.record === 'object'
                  ? (retry.result.record as Record<string, unknown>)
                  : null;
              const retryRecordId = String(retryRecord?.id || retryRecord?.record_id || '').trim();
              if (retryRecordId) {
                statePersistRecordIdRef.current = retryRecordId;
              }
            }
          } catch {
            // Keep runtime path resilient when state persistence fails.
          }
        }
      } finally {
        statePersistInFlightRef.current = false;
      }
    },
    [
      isToolOwner,
      requestDomSnapshot,
      requestTemplateState,
      resolveScreenStateExternalId,
      isSpawnedIframeMode,
      tool?.id,
      tool?.public,
    ]
  );

  const scheduleTemplateStatePersist = useCallback(
    (messageId: string, reason: string, stateOverride?: Record<string, unknown> | null) => {
      if (!tool?.id || tool.public || !isToolOwner) {
        return;
      }
      if (statePersistTimerRef.current) {
        window.clearTimeout(statePersistTimerRef.current);
      }
      statePersistTimerRef.current = window.setTimeout(() => {
        statePersistTimerRef.current = null;
        void persistTemplateStateSnapshot(messageId, reason, stateOverride);
      }, 700);
    },
    [isToolOwner, persistTemplateStateSnapshot, tool?.id, tool?.public]
  );

  const listAllRecordsByEntity = useCallback(
    async (
      currentToolId: string,
      entityType: string
    ): Promise<Record<string, unknown>[]> => {
      const normalizedEntityType = String(entityType || '').trim();
      if (!normalizedEntityType) {
        return [];
      }

      const records: Record<string, unknown>[] = [];
      let page = 1;
      const pageSize = 100;

      while (page <= 20) {
        const response = await toolRuntimeService.listRecords(currentToolId, {
          page,
          page_size: pageSize,
          entity_type: normalizedEntityType,
          include_inactive: false,
        });
        (response.items || []).forEach(item => {
          records.push(normalizeRuntimeRecord(item as unknown as Record<string, unknown>));
        });
        const totalPages = Number(response.total_pages || 1);
        if (page >= totalPages) {
          break;
        }
        page += 1;
      }

      return records;
    },
    []
  );

  const rehydrateRuntimeStateIntoIframe = useCallback(
    async (currentTool: ToolTemplate, runId: number) => {
      if (isSpawnedIframeMode) {
        return;
      }
      const runtimeEntityNames = extractRuntimeEntityNamesFromTool(currentTool);
      if (!runtimeEntityNames.length) {
        return;
      }

      const runtimeEntities: Record<string, Record<string, unknown>[]> = {};
      for (const entityName of runtimeEntityNames) {
        try {
          runtimeEntities[entityName] = await listAllRecordsByEntity(currentTool.id, entityName);
        } catch {
          runtimeEntities[entityName] = [];
        }
        if (runId !== rehydrationRunIdRef.current) {
          return;
        }
      }

      const latestScreenState = resolveLatestScreenStateRecord(
        runtimeEntities[SCREEN_STATE_ENTITY_TYPE] || [],
        currentTool.id
      );
      if (latestScreenState.recordId) {
        statePersistRecordIdRef.current = latestScreenState.recordId;
      }

      let templateStateResult = await requestTemplateState();
      if (!templateStateResult.responded || !templateStateResult.hasContract) {
        await wait(120);
        if (runId !== rehydrationRunIdRef.current) {
          return;
        }
        templateStateResult = await requestTemplateState();
      }
      if (runId !== rehydrationRunIdRef.current) {
        return;
      }

      if (templateStateResult.responded && !templateStateResult.hasContract) {
        showMissingContractErrorOnce();
        return;
      }
      if (!templateStateResult.responded) {
        return;
      }

      const baseState =
        latestScreenState.state && typeof latestScreenState.state === 'object'
          ? latestScreenState.state
          : templateStateResult.state;
      const mergedState = mergeTemplateStateWithRuntimeEntities(baseState, runtimeEntities);
      Object.entries(runtimeEntities).forEach(([entityName, records]) => {
        mergedState[entityName] = records;
      });

      const applyResult = await applyTemplateStateToIframe(mergedState, currentTool.id);
      if (runId !== rehydrationRunIdRef.current) {
        return;
      }
      if (applyResult.responded && !applyResult.hasContract) {
        showMissingContractErrorOnce();
      }
    },
    [
      applyTemplateStateToIframe,
      listAllRecordsByEntity,
      requestTemplateState,
      resolveLatestScreenStateRecord,
      showMissingContractErrorOnce,
      isSpawnedIframeMode,
    ]
  );

  const handleIframeLoad = useCallback(() => {
    if (!tool?.id) {
      return;
    }
    if (isSpawnedIframeMode) {
      return;
    }
    void toolRuntimeService
      .initRuntime(tool.id, { context: { source: 'tool_viewer' } })
      .then(runtimeInit => {
        runtimeStateTokenRef.current = String(runtimeInit.state_token || '').trim() || null;
      })
      .catch(() => {
        // Keep legacy flow when runtime init is unavailable.
      });
    rehydrationRunIdRef.current += 1;
    const runId = rehydrationRunIdRef.current;
    void rehydrateRuntimeStateIntoIframe(tool, runId);
  }, [rehydrateRuntimeStateIntoIframe, tool, isSpawnedIframeMode]);

  const handleCanvasAction = useCallback(
    (
      action: 'print' | 'copy' | 'download' | 'open_link' | 'email',
      payload?: {
        text?: string;
        href?: string;
        filename?: string;
        base64?: string;
        mimeType?: string;
      }
    ) => {
      if (action === 'copy') {
        const textToCopy = String(payload?.text || '').trim();
        if (!textToCopy) {
          return;
        }
        navigator.clipboard
          .writeText(textToCopy)
          .then(() => toolToast.success(t('common.copied')))
          .catch(() => toolToast.error(t('tools.saveValuesError')));
        return;
      }

      if (action === 'download') {
        const triggerBlobDownload = (
          blob: Blob,
          filename: string
        ): void => {
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
        };

        const preferredFilename = String(payload?.filename || '').trim();
        const preferredMime = String(payload?.mimeType || '').trim();
        const isCsvMime = /csv/i.test(preferredMime);
        const isPdfMime = /pdf/i.test(preferredMime);
        const defaultFilename = isCsvMime
          ? 'relatorio.csv'
          : isPdfMime
            ? 'relatorio.pdf'
            : 'arquivo.bin';

        const rawBase64 = String(payload?.base64 || '').trim();
        if (rawBase64) {
          try {
            const sanitizedBase64 = rawBase64
              .replace(/^data:[^;]+;base64,/i, '')
              .replace(/\s+/g, '');
            const binary = window.atob(sanitizedBase64);
            const bytes = new Uint8Array(binary.length);
            for (let index = 0; index < binary.length; index += 1) {
              bytes[index] = binary.charCodeAt(index);
            }
            triggerBlobDownload(
              new Blob([bytes], {
                type: preferredMime || 'application/octet-stream',
              }),
              preferredFilename || defaultFilename
            );
          } catch {
            toolToast.error(t('tools.saveValuesError'));
          }
          return;
        }

        const rawText = String(payload?.text || '');
        if (rawText) {
          const resolvedFilename =
            preferredFilename || (rawText.includes(',') ? 'relatorio.csv' : defaultFilename);
          const resolvedMime =
            preferredMime ||
            (resolvedFilename.toLowerCase().endsWith('.csv')
              ? 'text/csv;charset=utf-8;'
              : 'text/plain;charset=utf-8;');
          triggerBlobDownload(
            new Blob([rawText], { type: resolvedMime }),
            resolvedFilename
          );
          return;
        }

        const href = String(payload?.href || '').trim();
        if (!href || /^javascript:/i.test(href)) {
          return;
        }
        const link = document.createElement('a');
        link.href = href;
        if (payload?.filename) {
          link.download = payload.filename;
        } else if (/^data:/i.test(href) || /^blob:/i.test(href) || /pdf/i.test(href)) {
          link.download = 'arquivo.pdf';
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      if (action === 'email') {
        const href = String(payload?.href || '').trim();
        if (!href || !/^mailto:/i.test(href)) {
          return;
        }
        window.location.href = href;
        return;
      }

      if (action === 'open_link') {
        const href = String(payload?.href || '').trim();
        if (!href || href === '#' || /^javascript:/i.test(href)) {
          return;
        }
        const popup = window.open(href, '_blank', 'noopener,noreferrer');
        if (!popup) {
          window.location.assign(href);
        }
        return;
      }

      const currentHtml =
        iframeRef.current?.contentDocument?.documentElement?.outerHTML ||
        tool?.html_content ||
        '';
      printHtmlWithoutPopup(currentHtml, () => {
        toolToast.error(t('tools.saveValuesError'));
      });
    },
    [t, tool?.html_content]
  );

  const postRuntimeActionResult = useCallback(
    (
      messageId: string,
      payload: {
        request_id?: string;
        runtime_action?: RuntimeActionType;
        silent?: boolean;
        status: 'success' | 'error';
        message: string;
        result?: unknown;
        view_model_delta?: Record<string, unknown>;
        result_payload?: unknown;
        items?: unknown[];
        page?: number;
        page_size?: number;
        total?: number;
        total_pages?: number;
      }
    ) => {
      const compatiblePayload: Record<string, unknown> = { ...payload };
      if (payload.runtime_action) {
        compatiblePayload.action = payload.runtime_action;
      }
      if (payload.status === 'error' && payload.message) {
        compatiblePayload.error = payload.message;
      }
      iframeRef.current?.contentWindow?.postMessage(
        {
          source: 'devspott-canvas-parent',
          type: 'runtime_action_result',
          messageId,
          ...compatiblePayload,
        },
        '*'
      );
    },
    []
  );

  const handleRuntimeActionRequest = useCallback(
    async (messageId: string, payload: RuntimeActionMessagePayload) => {
      if (!tool?.id) {
        return;
      }

      const action = payload.runtime_action;
      const isTelemetryAction = action === 'runtime.telemetry';
      const requestId = String(payload.request_id || '').trim();
      if (!action) {
        postRuntimeActionResult(messageId, {
          request_id: requestId || undefined,
          status: 'error',
          message: 'runtime_action inválido.',
        });
        return;
      }

      const rawInputData =
        payload.data && typeof payload.data === 'object'
          ? payload.data
          : payload.payload && typeof payload.payload === 'object'
            ? payload.payload
            : {};

      const normalized = normalizeRuntimeActionRequest({
        action,
        entityType: toNonEmptyString(payload.entity_type) ?? singleEntityTypeFallback,
        recordId: payload.record_id,
        externalId: payload.external_id,
        data: rawInputData,
      });

      const actionData = resolveRuntimeActionData({
        rawData: rawInputData,
        normalizedData: normalized.data,
      });
      const coercedActionData = coerceRecordPayload({
        action,
        entityType: normalized.entityType,
        data: actionData,
        toolSchema: tool?.schema,
      });

      try {
        const runtimePayload: Record<string, unknown> = {
          ...(rawInputData as Record<string, unknown>),
          data: coercedActionData,
        };
        if (normalized.entityType) {
          runtimePayload.entity_type = normalized.entityType;
        }
        if (normalized.recordId) {
          runtimePayload.record_id = normalized.recordId;
        }
        if (normalized.externalId) {
          runtimePayload.external_id = normalized.externalId;
        }
        if (
          RUNTIME_BRIDGE_AUTO_IDEMPOTENCY_ENABLED &&
          action === 'record.create' &&
          !toNonEmptyString(runtimePayload.idempotency_key)
        ) {
          runtimePayload.idempotency_key = crypto.randomUUID();
        }
        const runtimeResponse = await toolRuntimeService.executeRuntimeAction(tool.id, {
          action_id: action,
          payload: runtimePayload,
          state_token: runtimeStateTokenRef.current || undefined,
        });
        runtimeStateTokenRef.current = String(runtimeResponse.state_token || '').trim() || null;
        const runtimeStatus = runtimeResponse.status === 'success' ? 'success' : 'error';
        const normalizedResultForIframe = normalizeRuntimeResultForIframe(
          action,
          runtimeResponse.result
        );
        postRuntimeActionResult(messageId, {
          request_id: requestId || undefined,
          runtime_action: action,
          silent: isTelemetryAction,
          status: runtimeStatus,
          message:
            runtimeStatus === 'success'
              ? resolveRuntimeActionSuccessMessage(action)
              : resolveRuntimeActionErrorMessage(action, 'Erro ao executar ação.'),
          result: normalizedResultForIframe,
          view_model_delta:
            runtimeResponse.view_model_delta &&
            typeof runtimeResponse.view_model_delta === 'object'
              ? runtimeResponse.view_model_delta
              : {},
        });
        if (
          runtimeStatus === 'success' &&
          !isTelemetryAction &&
          RUNTIME_ACTIONS_THAT_PERSIST_SCREEN_STATE.includes(action)
        ) {
          scheduleTemplateStatePersist(messageId, `runtime_action:${action}`);
        }

        if (action === 'tool.generate_artifact' && runtimeStatus === 'success') {
          const downloadUrl = runtimeResponse.result?.download_url;
          if (typeof downloadUrl === 'string' && downloadUrl) {
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = '';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }
        }
      } catch (error) {
        const baseMessage =
          error instanceof Error && error.message ? error.message : 'Erro ao executar ação.';
        postRuntimeActionResult(messageId, {
          request_id: requestId || undefined,
          runtime_action: action,
          silent: isTelemetryAction,
          status: 'error',
          message: resolveRuntimeActionErrorMessage(action, baseMessage),
        });
      }
    },
    [
      postRuntimeActionResult,
      scheduleTemplateStatePersist,
      singleEntityTypeFallback,
      tool?.id,
      tool?.schema,
    ]
  );

  useEffect(() => {
    if (!tool?.id) {
      return;
    }

    const messageId = `tool-${tool.id}`;
    const handleMessage = (event: MessageEvent) => {
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) {
        return;
      }

      const data = event.data as RuntimeActionMessagePayload & {
        source?: string;
        type?: string;
        messageId?: string;
        values?: Record<string, unknown>;
        html?: string;
        has_contract?: boolean;
        applied?: boolean;
        state?: Record<string, unknown> | null;
        action?: 'print' | 'copy' | 'download' | 'open_link' | 'email';
        text?: string;
        href?: string;
        filename?: string;
        base64?: string;
        mimeType?: string;
        status?: string;
        message?: string;
        visible?: boolean;
        select_text?: boolean;
      };

      if (data?.source !== 'devspott-canvas' || data?.messageId !== messageId) {
        return;
      }
      if (
        data?.type === 'canvas_action' &&
        data.action &&
        (data.action === 'print' ||
          data.action === 'copy' ||
          data.action === 'download' ||
          data.action === 'open_link' ||
          data.action === 'email')
      ) {
        handleCanvasAction(data.action, {
          text: data.text,
          href: data.href,
          filename: data.filename,
          base64: data.base64,
          mimeType: data.mimeType,
        });
        return;
      }
      if (data?.type === 'runtime_records_panel') {
        // O painel "Registros salvos" foi removido do drawer (refatoracao
        // da area do desenvolvedor para 2 abas: Embed + API). O canvas
        // renderiza a propria lista; ignoramos a mensagem em vez de quebrar.
        return;
      }
      if (data?.type === 'runtime_records_focus_search') {
        // Mantemos compat com tools antigas que disparam esse foco; sem
        // aba para focar, simplesmente ignoramos.
        return;
      }
      if (data?.type === 'tool_runtime_feedback') {
        const messageText = String(data.message || '').trim();
        const feedbackStatus = String(data.status || 'success');
        if (feedbackStatus === 'error') {
          toolToast.error(messageText || 'Erro');
        } else if (feedbackStatus === 'warn') {
          toolToast.warning(messageText || 'Atenção');
        } else if (feedbackStatus === 'info') {
          toolToast.info(messageText || '');
        } else {
          toolToast.success(messageText || 'OK');
        }
        return;
      }
      if (data?.type === 'runtime_action') {
        void handleRuntimeActionRequest(messageId, data);
        return;
      }
      if (data?.type === 'template_state') {
        if (templateStateResolverRef.current) {
          const resolve = templateStateResolverRef.current;
          templateStateResolverRef.current = null;
          resolve({
            hasContract: Boolean(data.has_contract),
            responded: true,
            state:
              data.state && typeof data.state === 'object'
                ? (data.state as Record<string, unknown>)
                : null,
          });
        }
        return;
      }
      if (data?.type === 'dom_snapshot') {
        if (domSnapshotResolverRef.current) {
          const resolve = domSnapshotResolverRef.current;
          domSnapshotResolverRef.current = null;
          resolve(typeof data.html === 'string' ? data.html : null);
        }
        return;
      }
      if (data?.type === 'template_state_applied') {
        if (templateStateApplyResolverRef.current) {
          const resolve = templateStateApplyResolverRef.current;
          templateStateApplyResolverRef.current = null;
          resolve({
            applied: Boolean(data.applied),
            hasContract: Boolean(data.has_contract),
            responded: true,
          });
        }
        return;
      }
      if (data?.type === 'runtime_action_result') {
        return;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleCanvasAction, handleRuntimeActionRequest, tool?.id]);

  return (
    <div
      ref={fullscreenRootRef}
      className={
        isToolViewportFill
          ? 'fixed inset-0 z-[90] flex min-h-0 w-full flex-col bg-white shadow-2xl dark:bg-gray-950'
          : 'flex h-[calc(100vh-5.5rem)] w-full min-h-0 flex-col gap-1'
      }
    >
      {isToolViewportFill && tool ? (
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
          <p className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-white">
            {tool.name}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsToolViewportFill(false)}
          >
            <Minimize2 className="h-4 w-4 sm:mr-1.5" aria-hidden />
            <span className="hidden sm:inline">{t('tools.exitFullscreen')}</span>
          </Button>
        </div>
      ) : null}
      <Card padding="none" className="w-full flex-1 min-h-0 p-0">
        <CardContent className="h-full p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <LoadingSpinner size="lg" />
            </div>
          ) : tool ? (
            <div className="h-full flex flex-col">
              <div className="flex-1 min-h-0 rounded-t-lg overflow-hidden">
                <iframe
                  ref={iframeRef}
                  key={`tool-${tool.id}`}
                  className="w-full h-full border-0 bg-white"
                  style={{ width: '100%', height: '100%' }}
                  sandbox={
                    isSpawnedIframeMode
                      ? 'allow-scripts allow-forms allow-same-origin'
                      : 'allow-scripts allow-forms'
                  }
                  src={isSpawnedIframeMode ? spawnedFrontendUrl : undefined}
                  srcDoc={isSpawnedIframeMode ? undefined : iframeDoc}
                  title={tool.name}
                  scrolling="auto"
                  onLoad={isSpawnedIframeMode ? undefined : handleIframeLoad}
                />
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-3 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('tools.notFound')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
      {tool && isToolOwner && (
        <>
          <div
            className={`fixed inset-0 transition-opacity duration-300 ${
              isToolViewportFill ? 'z-[91]' : 'z-40'
            } ${
              isDeveloperDrawerOpen
                ? 'bg-black/40 opacity-100 pointer-events-auto'
                : 'bg-black/0 opacity-0 pointer-events-none'
            }`}
            onClick={() => setIsDeveloperDrawerOpen(false)}
            aria-hidden={!isDeveloperDrawerOpen}
          />
          <aside
            className={`fixed right-0 top-0 h-full w-full max-w-2xl bg-white dark:bg-gray-900 shadow-2xl border-l border-gray-200 dark:border-gray-700 transition-transform duration-300 ease-out ${
              isToolViewportFill ? 'z-[92]' : 'z-50'
            } ${isDeveloperDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}
            role="dialog"
            aria-modal="true"
            aria-label={t('tools.appSettingsTitle')}
          >
            <div className="h-full flex flex-col">
              <div className="shrink-0 flex items-center justify-between px-3 py-3 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                  {t('tools.appSettingsTitle')}
                </h2>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsDeveloperDrawerOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex-1 min-h-0">
                <Tabs
                  tabs={settingsTabs}
                  activeTabId={activeSettingsTab}
                  onTabChange={setActiveSettingsTab}
                  className="h-full"
                />
              </div>
            </div>
          </aside>
        </>
      )}
    </div>
  );
};

export default ToolViewer;
